// Typed RPC client for mirror-agent's /api/rpc endpoint.
//
// All server calls go through this thin wrapper. It posts the
// `{ method, args, sessionId }` envelope, throws a friendly Error on
// network failure or `ok:false`, and surfaces 401 via a custom event so
// the app shell can show a "please log in" interstitial.
//
// The browser sends auth via the session cookie set by /login. In dev,
// Vite proxies /api → the test mirror-agent on :9005; in prod the new
// UI is served by mirror-agent itself so the cookie origin matches
// directly.

import { localStore } from "./local-store";
import { lookupPolicy } from "./cache-policy";
import { lookupMutator } from "./cache-mutators";

const RPC_URL = "/api/rpc";

export interface RpcEnvelope {
  method: string;
  args?: Record<string, unknown>;
  sessionId?: string;
}

export interface RpcOk { ok: true; [k: string]: unknown; }
export interface RpcErr { ok: false; error?: string; }

export class RpcError extends Error {
  constructor(public method: string, public status: number, message: string) {
    super(`[rpc ${method}] ${message}`);
  }
}

/**
 * Custom event dispatched on the window when an RPC returns 401. The
 * app shell listens for this and shows the login prompt — no module
 * needs to know about routing or auth state directly.
 */
export const RPC_UNAUTHORIZED_EVENT = "rpc:unauthorized";

let sessionId: string | null = null;
/**
 * The browser-side sessionId sent in every RPC body. mirror-agent uses
 * it to scope SSE delivery + concurrent-run limits. Generated lazily on
 * first call and reused for the rest of the page lifetime.
 */
function getSessionId(): string {
  if (sessionId) return sessionId;
  // Match the old client's id format ("ui-" + random) so server logs
  // don't get confused about which client a session belongs to.
  sessionId = "ui-" + Math.random().toString(36).slice(2, 10);
  return sessionId;
}

export function browserSessionId(): string {
  return getSessionId();
}

export async function rpc<T = RpcOk>(method: string, args: Record<string, unknown> = {}): Promise<T> {
  return wrappedRpc<T>(method, args, /* throwOnFalse */ true);
}

// True for failures we treat as "origin unreachable" — fetch itself
// threw (status 0) OR the server returned a 5xx, which on our stack
// typically means Cloudflare couldn't reach mirror-agent (502/503/504/
// 522/523/524) or mirror-agent crashed (500). Auth failures (401/403)
// and other 4xx errors deliberately do NOT count — those need to surface
// so the login flow runs and bad inputs aren't silently swallowed.
//
// Exported so hooks doing stale-while-revalidate can suppress error
// banners when a network failure happens on top of already-painted
// cached data — there's nothing for the user to act on, they're seeing
// their data.
export function isOfflineError(err: unknown): boolean {
  if (!(err instanceof RpcError)) return false;
  return err.status === 0 || err.status >= 500;
}

const isUnreachable = isOfflineError;

async function wrappedRpc<T>(method: string, args: Record<string, unknown>, throwOnFalse: boolean): Promise<T> {
  const policy = lookupPolicy(method);
  // Read policy: network-first, fall back to cached response on a
  // true network failure (server unreachable / browser offline).
  // Auth failures and other non-network errors are NOT swallowed —
  // those need to propagate so the LoginView fires.
  if (policy && policy.kind === "read" && policy.store && policy.key) {
    const key = policy.key(args);
    try {
      const fresh = await rpcCore<T>(method, args, throwOnFalse);
      localStore.put(policy.store, { key, value: fresh as unknown, fetchedAt: Date.now() })
        .catch((err) => console.warn(`[rpc-cache] put ${method} failed:`, err?.message || err));
      return fresh;
    } catch (err: any) {
      if (!isUnreachable(err)) throw err;
      const cached = await localStore.get<T>(policy.store!, key).catch(() => undefined);
      if (cached) {
        console.info(`[rpc-cache] serving cached ${method} (origin unreachable)`);
        return cached.value as T;
      }
      throw err;
    }
  }
  // Write policy: try network; on a network failure, queue + return
  // a synthesized response so hooks keep rendering. Auth/server
  // errors still surface (we don't queue what the server rejected).
  // This branch runs for both rpc() and rpcRaw() so endpoints like
  // notes.save (which uses rpcRaw to preserve the conflict payload)
  // also get the offline-queue treatment.
  if (policy && policy.kind === "write") {
    const mutator = lookupMutator(method);
    try {
      const resp = await rpcCore<T>(method, args, throwOnFalse);
      // For rpcRaw paths, resp may be `{ ok: false, ...conflict }`.
      // Only run the read-cache mutator when the server actually
      // accepted the write — a conflict response shouldn't mutate
      // the read cache with stale optimistic data.
      const wasAccepted = !resp || (resp as any).ok !== false;
      if (mutator && wasAccepted) {
        mutator(args, resp as unknown as Record<string, any>)
          .catch((e) => console.warn(`[rpc-cache] mutate ${method} failed:`, e?.message || e));
      }
      return resp;
    } catch (err: any) {
      if (!isUnreachable(err)) throw err;
      localStore.enqueueWrite(method, args)
        .then(() => console.info(`[rpc-cache] queued ${method} (offline)`))
        .catch((e) => console.warn(`[rpc-cache] enqueue ${method} failed:`, e?.message || e));
      const synthesized = policy.synthesize
        ? policy.synthesize(args)
        : { ok: true, _queued: true };
      // Await the mutator on the queued path so callers that
      // immediately re-read (e.g. n.create → refreshNotes) see the
      // patched cache. IDB writes are sub-millisecond — the latency
      // is negligible and trading it for correctness is the right call.
      if (mutator) {
        await mutator(args, synthesized as Record<string, any>)
          .catch((e) => console.warn(`[rpc-cache] mutate ${method} failed:`, e?.message || e));
      }
      return synthesized as T;
    }
  }
  return rpcCore<T>(method, args, throwOnFalse);
}

// Peek at the cache for a read-policy RPC without firing a network
// request. Used by hooks doing stale-while-revalidate: paint cached
// state instantly on mount, then let the regular network call (via
// rpc()) update with fresh data. The wrapper above already keeps the
// cache populated on every successful read + write — so any endpoint
// the user has touched before is renderable from cache offline OR for
// instant first paint.
//
// Returns the cached *envelope* (e.g. `{ ok: true, lists: [...] }`),
// matching what rpc() resolves with. Callers unwrap the same way they
// do after rpc(). Returns null on cache miss or non-read policy.
export async function peekCache<T>(
  method: string,
  args: Record<string, unknown> = {},
): Promise<T | null> {
  const policy = lookupPolicy(method);
  if (!policy || policy.kind !== "read" || !policy.store || !policy.key) return null;
  try {
    const entry = await localStore.get<T>(policy.store, policy.key(args));
    return (entry?.value as T) ?? null;
  } catch {
    return null;
  }
}

// rpcRaw — same wire path as rpc(), but returns the full response body
// even when `ok: false`. Use this for endpoints that pack actionable
// data into the failure payload (e.g. notes.save returning
// `currentSha`/`currentContent` on a conflict so the editor can show a
// merge UI). The default `rpc()` throws on `ok:false` and loses those
// fields. Cache policy still applies — offline writes queue and
// reads fall back to IDB — but the raw response is preserved.
export async function rpcRaw<T = unknown>(method: string, args: Record<string, unknown> = {}): Promise<T> {
  return wrappedRpc<T>(method, args, /* throwOnFalse */ false);
}

async function rpcCore<T>(method: string, args: Record<string, unknown>, throwOnFalse: boolean): Promise<T> {
  const body: RpcEnvelope = { method, args, sessionId: getSessionId() };
  let res: Response;
  try {
    res = await fetch(RPC_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // CSRF defense — the server rejects /api/rpc POSTs that don't
        // carry this header so cross-origin pages can't trigger an RPC
        // even with credentials. Value is a presence check, not a
        // token (see web_server.mjs around the "missing X-Mirror-RPC"
        // guard).
        "X-Mirror-RPC": "1",
      },
      body: JSON.stringify(body),
      credentials: "include",
    });
  } catch (netErr: any) {
    throw new RpcError(method, 0, netErr?.message || "network failed");
  }
  if (res.status === 401) {
    try { window.dispatchEvent(new CustomEvent(RPC_UNAUTHORIZED_EVENT)); } catch {}
    throw new RpcError(method, res.status, "not authenticated");
  }
  if (res.status === 403) {
    // 403 is a real authorization error (scope / host gate), NOT a missing
    // session — must NOT bounce the user to the Auth0 login flow.
    let detail = "";
    try { detail = await res.text(); } catch {}
    throw new RpcError(method, 403, detail || "forbidden");
  }
  if (!res.ok) {
    let detail = "";
    try { detail = await res.text(); } catch {}
    throw new RpcError(method, res.status, detail || `${res.status} ${res.statusText}`);
  }
  let json: any;
  try { json = await res.json(); }
  catch { throw new RpcError(method, res.status, "invalid JSON response"); }
  if (throwOnFalse && json && json.ok === false) {
    throw new RpcError(method, res.status, json.error || "request failed");
  }
  return json as T;
}

// ── Strongly-typed wrappers for known endpoints ──────────────────────────

export interface Session {
  id: string;
  name: string;
  kind: "chat" | "coding";
  updatedAt: string;
  messageCount: number;
  preview?: string;
}

export interface DownloadFile {
  name: string;
  size: number;
  /** Modified time as ms epoch. */
  mtimeMs: number;
}

/** Latest Android APK metadata for in-app update checks. Tag-only:
 *  server filters to clean tagged-release filenames, so this is
 *  populated only when a real release is cut. The wrapper compares
 *  its embedded BuildConfig.VERSION_NAME against `version` via
 *  semver-gt; banner fires only when server is strictly newer. */
export interface AppLatestAndroid {
  available: boolean;
  version?: string;
  filename?: string;
  size?: number;
  mtimeMs?: number;
  downloadUrl?: string;
}

/** Metadata for one uploaded device diagnostic log (admin list view).
 *  Snapshot bytes intentionally omitted — fetch the full row with
 *  adminGetDeviceLog(id) to read the actual log text. */
export interface DeviceLogSummary {
  id: number;
  userId: string;
  username?: string;
  deviceId: string;
  versionName?: string | null;
  createdAt: string;
  snapshotBytes: number;
}

/** Full device log row including the snapshot body. */
export interface DeviceLog {
  id: number;
  userId: string;
  username?: string;
  deviceId: string;
  versionName?: string | null;
  snapshot: string;
  createdAt: string;
}

/** One hit from sessionSearch — semantic (RAG via TEI+Qdrant+rerank)
 *  with FTS fallback. `idx` is the message index inside the thread the
 *  match landed in, used by the sidebar to deep-link / scroll into the
 *  conversation. `source` discriminates path: "semantic" results were
 *  vector-matched, "fts" results came from the keyword fallback. */
export interface SessionSearchHit {
  id: string;
  name: string;
  kind: "chat" | "coding";
  role: "user" | "assistant" | "system" | "tool";
  idx: number;
  updatedAt: string;
  /** Pre-highlighted HTML (server wraps the match in <mark>). */
  excerpt: string;
  rank: number;
  matchCount: number;
  source: "semantic" | "fts";
}

export type PantryCategory =
  | "Produce" | "Meat & seafood" | "Dairy & fridge" | "Bread & bakery"
  | "Pasta & grains" | "Cans" | "Sauces & jars" | "Oils & vinegars"
  | "Spices & dry" | "Snacks" | "Beverages" | "Frozen" | "Other";

export interface PantryItem {
  id: number;
  userId: string;
  name: string;
  /** Fuzzy human-readable quantity ("half jar", "2 cans", "almost gone"). */
  qty: string;
  category: PantryCategory;
  location: string | null;
  /** ISO date YYYY-MM-DD or null (shelf-stable / unknown). */
  expires: string | null;
  lowStock: boolean;
  source: "manual" | "shopping_checkoff" | "cook_mode";
  createdAt: string;
  updatedAt: string;
}

/** A single in-app notification, surfaced by the topbar bell.
 *  Producers (pantry expiry cron, admin announcements, etc.) drop
 *  rows with their own `kind` and `payload` shape; the bell routes
 *  to category tabs based on `kind` prefix. */
export interface Notification {
  id: number;
  kind: string;            // "pantry.expiring", "pantry.expired", "admin.*", etc.
  payload: Record<string, any>;
  dedupKey: string | null;
  createdAt: string;
  readAt: string | null;
  dismissedAt: string | null;
}

export interface NotificationSummary {
  total: number;
  unread: number;
}

export interface PantrySummary {
  total: number;
  lowStock: number;
  expired: number;
  /** Items expiring within the next 3 days inclusive. */
  expiringSoon: number;
}

/** Each entry the pantry-gate filtered out of a recipes.addToList call.
 *  Lets the toast show "Skipped 2 in pantry — add anyway" and the
 *  override path re-runs the call with usePantry:false. */
export interface PantryGateSkip {
  text: string;
  quantity?: string | null;
  pantryItemId: number;
  /** Fuzzy qty from the pantry row, e.g. "half jar". Helps the user
   *  decide whether the skip is reasonable. */
  pantryQty: string;
}

export interface RecipeAddToListResult {
  /** Shopping items the server actually created. */
  added: Array<{ id: number; text: string; quantity: string | null; [k: string]: any }>;
  /** Ingredients dropped due to a non-pantry error (rare — bad text,
   *  list permission, etc). Distinct from pantrySkipped. */
  skipped: Array<{ text: string; error?: string; [k: string]: any }>;
  pantrySkipped: PantryGateSkip[];
  recipeName?: string;
  listName?: string;
  /** How many seasonings the user opted to add (0 unless includeSeasonings). */
  seasoningsAdded?: number;
}

/** One seasoning a recipe calls for, with the household's stock state.
 *  Returned by recipes.seasoningsFor to drive the add-to-list picker. */
export interface RecipeSeasoning {
  /** Canonical catalog name, e.g. "Black pepper". */
  name: string;
  /** The ingredient text as written in the recipe ("ground black pepper"). */
  text: string;
  quantity?: string | null;
  /** Whether the household keeps this seasoning on hand. */
  stocked: boolean;
}

/** A row in the household seasonings catalog (the "spice rack" picker). */
export interface SeasoningCatalogItem {
  name: string;
  /** Cosmetic cluster, e.g. "Everyday", "Dried herbs", "Your own". */
  group: string;
  stocked: boolean;
  /** Whether the household has an explicit decision for this one. */
  configured: boolean;
  /** Household-added seasoning (removable); false for catalog entries. */
  custom: boolean;
}

export interface SessionMessage {
  role: "user" | "assistant" | "system" | "tool";
  content: string | MessageContentPart[];
  tool_calls?: ToolCall[];
  tool_call_id?: string;
  attachments?: unknown[];
  /** Model's chain-of-thought from `delta.reasoning` / `.reasoning_content`.
   *  Populated only on assistant messages where the LLM emitted a
   *  reasoning block. Lets the chat UI render the thought process in a
   *  collapsible row alongside the assistant's visible output. */
  reasoning_content?: string;
  _seq?: number;
}

export interface MessageContentPart {
  type: "text" | "image" | "image_url";
  text?: string;
  image_url?: { url: string };
}

export interface ToolCall {
  id: string;
  function: { name: string; arguments: string };
}

export interface EnvDefaults {
  userId: string;
  isAdmin: boolean;
  /** Session scope: "standard" (adult), "kid", or "kid-device" (shared
   *  family tablet → PIN picker). */
  scope?: "standard" | "kid" | "kid-device";
  cwd: string;
  home: string;
  baseUrl: string;
  model: string;
  maxContext: number;
}

export const api = {
  async sessionList(): Promise<Session[]> {
    const r = await rpc<{ ok: true; sessions: Session[] }>("session.list");
    return r.sessions || [];
  },
  async authWhoami(): Promise<{
    id: string; isAdmin: boolean; email: string | null;
    displayName: string; avatarColor: string | null; avatarEmoji: string | null;
    age: number | null; role: "admin" | "adult" | "kid"; scope: string;
  }> {
    const r = await rpc<{
      ok: true; id: string; isAdmin: boolean; email: string | null;
      displayName?: string; avatarColor?: string | null; avatarEmoji?: string | null;
      age?: number | null; role?: "admin" | "adult" | "kid"; scope?: string;
    }>("auth.whoami");
    return {
      id: r.id,
      isAdmin: !!r.isAdmin,
      email: r.email || null,
      displayName: r.displayName || r.id,
      avatarColor: r.avatarColor || null,
      avatarEmoji: r.avatarEmoji || null,
      age: r.age ?? null,
      role: r.role || (r.isAdmin ? "admin" : "adult"),
      scope: r.scope || "standard",
    };
  },
  async downloadsList(): Promise<DownloadFile[]> {
    const r = await rpc<{ ok: true; files: DownloadFile[] }>("downloads.list");
    return r.files || [];
  },
  async appLatestAndroid(): Promise<AppLatestAndroid> {
    const r = await rpc<{ ok: true } & AppLatestAndroid>("app.latestAndroid");
    return {
      available: r.available || false,
      version: r.version,
      filename: r.filename,
      size: r.size,
      mtimeMs: r.mtimeMs,
      downloadUrl: r.downloadUrl,
    };
  },
  async sessionDelete(id: string): Promise<void> {
    await rpc("session.delete", { id });
  },
  // Semantic search across the user's chat sessions. Server falls back
  // to FTS when the vector index is down/disabled, so this always
  // returns *something* — never call session.searchText directly.
  // Excerpts are pre-highlighted HTML (the renderer marks the matched
  // span); render via dangerouslySetInnerHTML.
  async sessionSearch(query: string, limit = 25): Promise<SessionSearchHit[]> {
    const r = await rpc<{ ok: true; hits: SessionSearchHit[] }>("session.searchSemantic", { query, limit });
    return r.hits || [];
  },
  // ─── Pantry ────────────────────────────────────────────────────
  // Per-user "what's at home" list. Auto-expiry is server-side
  // (pass undefined to let inferExpiry run; pass null to suppress).
  async pantryList(): Promise<PantryItem[]> {
    const r = await rpc<{ ok: true; items: PantryItem[] }>("pantry.list");
    return r.items || [];
  },
  async pantryAdd(args: {
    name: string;
    qty?: string;
    category?: PantryCategory;
    location?: string | null;
    expires?: string | null;
    lowStock?: boolean;
    source?: PantryItem["source"];
  }): Promise<PantryItem> {
    const r = await rpc<{ ok: true; item: PantryItem }>("pantry.add", args);
    return r.item;
  },
  async pantryUpdate(itemId: number, patch: Partial<Omit<PantryItem, "id" | "userId" | "createdAt" | "updatedAt" | "source">>): Promise<PantryItem> {
    const r = await rpc<{ ok: true; item: PantryItem }>("pantry.update", { itemId, ...patch });
    return r.item;
  },
  async pantryDelete(itemId: number): Promise<void> {
    await rpc("pantry.delete", { itemId });
  },
  async pantrySummary(): Promise<PantrySummary> {
    const r = await rpc<{ ok: true; summary: PantrySummary }>("pantry.summary");
    return r.summary;
  },
  // ─── Seasonings (household spice rack) ─────────────────────────
  async seasoningsCatalog(): Promise<SeasoningCatalogItem[]> {
    const r = await rpc<{ ok: true; items: SeasoningCatalogItem[] }>("seasonings.catalog");
    return r.items || [];
  },
  async seasoningsSetStocked(name: string, stocked: boolean): Promise<void> {
    await rpc("seasonings.setStocked", { name, stocked });
  },
  /** Replace the whole stocked set in one write (everything not listed
   *  becomes not-stocked, which also marks the household "configured"). */
  async seasoningsSetAll(stocked: string[]): Promise<void> {
    await rpc("seasonings.setAll", { stocked });
  },
  /** Add a household's own seasoning (starts stocked). Returns its name. */
  async seasoningsAddCustom(name: string): Promise<string> {
    const r = await rpc<{ ok: true; name: string }>("seasonings.addCustom", { name });
    return r.name;
  },
  /** Remove a household custom seasoning (catalog entries can't be removed). */
  async seasoningsRemoveCustom(name: string): Promise<void> {
    await rpc("seasonings.removeCustom", { name });
  },
  // Commit a Cook Mode "any left?" sweep — updates qty / lowStock for
  // partial-use picks AND deletes "all gone" picks (optionally pushing
  // them to a shopping list so the restock loop closes in one tap).
  // ─── Notifications (bell dropdown) ─────────────────────────────
  async notificationsList(opts: { limit?: number; includeRead?: boolean } = {}): Promise<Notification[]> {
    const r = await rpc<{ ok: true; items: Notification[] }>("notifications.list", opts);
    return r.items || [];
  },
  async notificationsSummary(): Promise<NotificationSummary> {
    const r = await rpc<{ ok: true; summary: NotificationSummary }>("notifications.summary");
    return r.summary;
  },
  async notificationsMarkRead(id: number): Promise<void> {
    await rpc("notifications.markRead", { id });
  },
  async notificationsMarkAllRead(): Promise<void> {
    await rpc("notifications.markAllRead");
  },
  async notificationsDismiss(id: number): Promise<void> {
    await rpc("notifications.dismiss", { id });
  },

  async pantryBulkUpdate(args: {
    updates?: { itemId: number; patch: Partial<PantryItem> }[];
    removes?: { itemId: number; autoAddToShopping?: boolean }[];
    /** New for the always-fire cook sweep: ingredients that weren't in
     *  pantry yet but the user wants to start tracking after cooking.
     *  Server calls pantry.add for each. */
    creates?: { name: string; qty?: string | null; lowStock?: boolean; source?: string }[];
    autoShopListId?: number | null;
  }): Promise<{ updated: PantryItem[]; removed: PantryItem[]; created: PantryItem[]; shopAdded: Array<{ id: number; text: string }> }> {
    const r = await rpc<{ ok: true; updated: PantryItem[]; removed: PantryItem[]; created?: PantryItem[]; shopAdded: any[] }>("pantry.bulkUpdate", args);
    return { updated: r.updated || [], removed: r.removed || [], created: r.created || [], shopAdded: r.shopAdded || [] };
  },
  async sessionLoad(id: string): Promise<{ messages: SessionMessage[]; name: string; model: string; kind: string }> {
    const r = await rpc<any>("session.load", { id });
    // Server returns { ok, session: { messages, name, model, kind, ... } }
    // not a flat object — unwrap. Be tolerant of either shape so a
    // future flatten doesn't silently break the client.
    const s = r.session || r;
    return {
      messages: s.messages || [],
      name: s.name || "",
      model: s.model || "",
      kind: s.kind || "chat",
    };
  },
  async envDefaults(): Promise<EnvDefaults> {
    return await rpc<EnvDefaults & RpcOk>("env.defaults") as unknown as EnvDefaults;
  },
  async runStart(args: {
    userMessage: string;
    attachments?: Array<{ kind: "image"; dataUrl: string; name?: string; mime?: string }>;
    mode?: string;
    conversation?: SessionMessage[];
    savedSessionId?: string;
    noThinking?: boolean;
    systemPromptAppend?: string;
    /** Voice/QC mode — server-side filters the tool registry to a
     *  household-only allowlist (no bash, no file/code tools, no memory
     *  writes). Independent of `mode` (which drives permissionMode on
     *  the agent loop). Set true from the QC voice flow only. */
    voiceMode?: boolean;
  }): Promise<{ runId?: string }> {
    return await rpc<{ ok: true; runId?: string }>("run.start", args) as any;
  },
  async runAbort(runId: string): Promise<void> {
    await rpc("run.abort", { runId });
  },
  async runApprove(approvalId: string, approved: boolean, reason?: string): Promise<void> {
    await rpc("run.approve", { approvalId, approved, reason });
  },
  async runAnswer(questionId: string, answer: string): Promise<void> {
    await rpc("run.answer", { questionId, answer });
  },

  // Conversation compaction — summarises older turns into a synthetic
  // assistant message to reclaim context. Returns the new messages
  // array; callers should replace their local conversation with it.
  async conversationCompact(savedSessionId: string | undefined, conversation: SessionMessage[]): Promise<{ messages: SessionMessage[]; before: number; after: number }> {
    const r = await rpc<any>("conversation.compact", {
      savedSessionId, conversation,
    });
    return { messages: r.messages || [], before: r.before || 0, after: r.after || 0 };
  },

  // Public share links — one active token per session.
  async sessionShareGet(id: string): Promise<{ token: string; createdAt: number; url: string } | null> {
    const r = await rpc<any>("session.share.get", { id });
    return r.share || null;
  },
  async sessionShareCreate(id: string): Promise<{ token: string; createdAt: number; url: string }> {
    const r = await rpc<any>("session.share.create", { id });
    return r.share;
  },
  async sessionShareRevoke(id: string): Promise<void> {
    await rpc("session.share.revoke", { id });
  },

  // --- Skills (inventory; server supports save+delete too) ---
  async skillsList(): Promise<Skill[]> {
    const r = await rpc<{ ok: true; skills: Skill[] }>("skills.list");
    return r.skills || [];
  },

  // --- Memory (cross-session notes the agent reads/writes itself) ---
  async memoryList(): Promise<MemoryMeta[]> {
    const r = await rpc<{ ok: true; memories: MemoryMeta[] }>("memory.list");
    return r.memories || [];
  },
  async memoryGet(name: string): Promise<Memory | null> {
    const r = await rpc<any>("memory.get", { name });
    return r.memory || null;
  },
  async memorySave(args: { name: string; description: string; type: MemoryType; body: string }): Promise<void> {
    await rpc("memory.save", args);
  },
  async memoryDelete(name: string): Promise<boolean> {
    const r = await rpc<{ ok: boolean }>("memory.delete", { name });
    return !!r.ok;
  },

  // --- Prompt presets ---
  async presetsList(): Promise<PresetMeta[]> {
    const r = await rpc<{ ok: true; presets: PresetMeta[] }>("presets.list");
    return r.presets || [];
  },
  async presetGet(name: string): Promise<Preset | null> {
    const r = await rpc<any>("presets.get", { name });
    return r.preset || null;
  },
  async presetSave(args: { name: string; description: string; body: string }): Promise<void> {
    await rpc("presets.save", args);
  },
  async presetDelete(name: string): Promise<boolean> {
    const r = await rpc<{ ok: boolean }>("presets.delete", { name });
    return !!r.ok;
  },

  // --- To-dos ---
  async todoLists(): Promise<TodoList[]> {
    const r = await rpc<{ ok: true; lists: TodoList[] }>("todos.listLists");
    return r.lists || [];
  },
  async todoCreateList(name: string): Promise<TodoList> {
    const r = await rpc<{ ok: true; list: TodoList }>("todos.createList", { name });
    return r.list;
  },
  async todoListItems(listId: string): Promise<TodoItem[]> {
    const r = await rpc<{ ok: true; items: TodoItem[] }>("todos.listItems", { listId });
    return r.items || [];
  },
  async todoAdd(args: Partial<TodoItem> & { listId: string; title: string }): Promise<TodoItem> {
    const r = await rpc<{ ok: true; item: TodoItem }>("todos.addItem", args);
    return r.item;
  },
  async todoUpdate(itemId: string, patch: Partial<TodoItem>): Promise<TodoItem> {
    const r = await rpc<{ ok: true; item: TodoItem }>("todos.updateItem", { itemId, ...patch });
    return r.item;
  },
  async todoDelete(itemId: string): Promise<void> {
    await rpc("todos.deleteItem", { itemId });
  },
  async todoClearDone(listId: string): Promise<number> {
    const r = await rpc<{ ok: true; deleted: number }>("todos.clearDone", { listId });
    return r.deleted || 0;
  },
  async todoTagsList(): Promise<TodoTag[]> {
    const r = await rpc<{ ok: true; tags: TodoTag[] }>("todos.tagsList");
    return r.tags || [];
  },
  async todoTagCreate(name: string): Promise<TodoTag> {
    const r = await rpc<{ ok: true; tag: TodoTag }>("todos.tagCreate", { name });
    return r.tag;
  },
  async todoTagRename(tagId: number, name: string): Promise<TodoTag> {
    const r = await rpc<{ ok: true; tag: TodoTag }>("todos.tagRename", { tagId, name });
    return r.tag;
  },
  async todoTagDelete(tagId: number): Promise<{ affectedItems: number }> {
    const r = await rpc<{ ok: true; affectedItems: number }>("todos.tagDelete", { tagId });
    return { affectedItems: r.affectedItems || 0 };
  },
  async todoTagReorder(ids: number[]): Promise<TodoTag[]> {
    const r = await rpc<{ ok: true; tags: TodoTag[] }>("todos.tagReorder", { ids });
    return r.tags || [];
  },

  // --- Recipe tags (user-managed taxonomy) ---
  async recipesTagsList(): Promise<RecipeTag[]> {
    const r = await rpc<{ ok: true; tags: RecipeTag[] }>("recipes.tagsList");
    return r.tags || [];
  },
  async recipesTagCreate(name: string, category: RecipeTagCategory): Promise<RecipeTag> {
    const r = await rpc<{ ok: true; tag: RecipeTag }>("recipes.tagCreate", { name, category });
    return r.tag;
  },
  async recipesTagRename(tagId: number, name: string): Promise<RecipeTag> {
    const r = await rpc<{ ok: true; tag: RecipeTag }>("recipes.tagRename", { tagId, name });
    return r.tag;
  },
  async recipesTagSetCategory(tagId: number, category: RecipeTagCategory): Promise<RecipeTag> {
    const r = await rpc<{ ok: true; tag: RecipeTag }>("recipes.tagSetCategory", { tagId, category });
    return r.tag;
  },
  async recipesTagDelete(tagId: number): Promise<{ affectedRecipes: number }> {
    const r = await rpc<{ ok: true; affectedRecipes: number }>("recipes.tagDelete", { tagId });
    return { affectedRecipes: r.affectedRecipes || 0 };
  },
  async recipesTagReorder(category: RecipeTagCategory, ids: number[]): Promise<RecipeTag[]> {
    const r = await rpc<{ ok: true; tags: RecipeTag[] }>("recipes.tagReorder", { category, ids });
    return r.tags || [];
  },

  // --- Households (v49) ---
  async householdsList(): Promise<{ households: Household[]; activeHouseholdId: number | null }> {
    const r = await rpc<{ ok: true; households: Household[]; activeHouseholdId: number | null }>("households.list");
    return { households: r.households || [], activeHouseholdId: r.activeHouseholdId ?? null };
  },
  async householdsCreate(name: string): Promise<Household> {
    const r = await rpc<{ ok: true; household: Household }>("households.create", { name });
    return r.household;
  },
  async householdsRename(householdId: number, name: string): Promise<Household> {
    const r = await rpc<{ ok: true; household: Household }>("households.rename", { householdId, name });
    return r.household;
  },
  async householdsDelete(householdId: number): Promise<void> {
    await rpc("households.delete", { householdId });
  },
  async householdsListMembers(householdId: number): Promise<HouseholdMember[]> {
    const r = await rpc<{ ok: true; members: HouseholdMember[] }>("households.listMembers", { householdId });
    return r.members || [];
  },
  /** Family Stars Bar — total stars (all-time, kid + adult chores) per
   *  household member. Excludes denied kid completions and kid-device
   *  pseudo-users. Caller must be a member. */
  async householdsStarsByMember(householdId?: number): Promise<FamilyStarsMember[]> {
    const r = await rpc<{ ok: true; members: FamilyStarsMember[] }>("households.starsByMember", { householdId });
    return r.members || [];
  },
  async householdsAddMember(householdId: number, userId: string, role: "owner" | "admin" | "member" = "member"): Promise<HouseholdMember[]> {
    const r = await rpc<{ ok: true; members: HouseholdMember[] }>("households.addMember", { householdId, userId, role });
    return r.members || [];
  },
  async householdsRemoveMember(householdId: number, userId: string): Promise<HouseholdMember[]> {
    const r = await rpc<{ ok: true; members: HouseholdMember[] }>("households.removeMember", { householdId, userId });
    return r.members || [];
  },
  // Owner-only: turn a plain member into an admin (full management
  // rights minus destructive powers — delete, transfer ownership,
  // promote / demote other admins).
  async householdsPromoteAdmin(householdId: number, userId: string): Promise<HouseholdMember[]> {
    const r = await rpc<{ ok: true; members: HouseholdMember[] }>("households.promoteAdmin", { householdId, userId });
    return r.members || [];
  },
  // Owner-only: demote an admin back to plain member.
  async householdsDemoteAdmin(householdId: number, userId: string): Promise<HouseholdMember[]> {
    const r = await rpc<{ ok: true; members: HouseholdMember[] }>("households.demoteAdmin", { householdId, userId });
    return r.members || [];
  },
  async householdsLeave(householdId: number): Promise<void> {
    await rpc("households.leave", { householdId });
  },
  async householdsSetActive(householdId: number): Promise<void> {
    await rpc("households.setActive", { householdId });
  },

  // Add a kid to a household — a local profile under the household's
  // shared kid-device login. `pin` is optional: with it the kid is gated
  // in the picker, without it they're tappable straight through once the
  // family-tablet account is signed in. No per-kid Auth0 account.
  async householdsAddKid(householdId: number, args: {
    id?: string;
    displayName: string;
    pin?: string;
    avatarColor?: string | null;
    avatarEmoji?: string | null;
    age?: number | null;
  }): Promise<{ kidId: string; displayName: string }> {
    const r = await rpc<{ ok: true; kidId: string; displayName: string }>("households.addKid", { householdId, ...args });
    return { kidId: r.kidId, displayName: r.displayName };
  },
  async usersSetPin(targetUserId: string | null, pin: string): Promise<void> {
    await rpc("users.setPin", { targetUserId, pin });
  },

  // Household shared kid-device login — one Auth0 account per household
  // that the family types into their tablet, then kids pick by PIN (or
  // tap straight through if PIN-less). Owner-only.
  async householdsProvisionKidDevice(householdId: number, handle: string, password: string): Promise<{ email: string; auth0UserId: string }> {
    const r = await rpc<{ ok: true; email: string; auth0UserId: string }>("households.provisionKidDevice", { householdId, handle, password });
    return { email: r.email, auth0UserId: r.auth0UserId };
  },
  async householdsResetKidDevicePassword(householdId: number, password: string): Promise<{ email: string }> {
    const r = await rpc<{ ok: true; email: string }>("households.resetKidDevicePassword", { householdId, password });
    return { email: r.email };
  },
  async householdsKidDeviceStatus(householdId: number): Promise<{ provisioned: boolean; email: string }> {
    const r = await rpc<{ ok: true; provisioned: boolean; email: string }>("households.kidDeviceStatus", { householdId });
    return { provisioned: r.provisioned, email: r.email };
  },
  async usersSetProfile(args: { targetUserId?: string | null; displayName?: string; avatarColor?: string | null; avatarEmoji?: string | null; age?: number | null }): Promise<UserProfile | null> {
    const r = await rpc<{ ok: true; profile: UserProfile | null }>("users.setProfile", args);
    return r.profile;
  },
  // Public endpoints — used by the kid-login screen on chat2 to render
  // the avatar grid + submit a PIN. Server returns 404 on the public
  // hostname (chat.beemerworks.com) per the host gate, so a 404 here
  // is the signal to hide kid login affordance entirely.
  async kidLoginList(): Promise<KidLoginEntry[]> {
    const r = await fetch("/api/kid-login", { method: "GET", credentials: "include" });
    if (!r.ok) throw new Error("kid login not available on this host");
    const data = await r.json();
    return (data.kids || []) as KidLoginEntry[];
  },
  async kidLogin(id: string, pin?: string): Promise<void> {
    const r = await fetch("/api/kid-login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ id, pin: pin || "" }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({ error: "login failed" }));
      throw new Error(err.error || "login failed");
    }
  },
  // "Switch user" from the kid app: drop back to the household's shared
  // device session (the PIN picker) without a full logout. Returns
  // "device" when the device session was restored (caller reloads into
  // the picker) or "loggedout" when there's no device account (caller
  // falls back to the LAN picker / login).
  async kidSwitch(): Promise<"device" | "loggedout"> {
    const r = await fetch("/api/kid-switch", {
      method: "POST",
      credentials: "include",
      headers: { "X-Mirror-RPC": "1" },
    });
    if (!r.ok) throw new Error("switch failed");
    const d = await r.json().catch(() => ({}));
    return d.mode === "device" ? "device" : "loggedout";
  },

  // --- Admin: households ---
  async adminHouseholdsList(): Promise<AdminHousehold[]> {
    const r = await rpc<{ ok: true; households: AdminHousehold[] }>("admin.householdsList");
    return r.households || [];
  },
  async adminHouseholdMembers(householdId: number): Promise<HouseholdMember[]> {
    const r = await rpc<{ ok: true; members: HouseholdMember[] }>("admin.householdMembers", { householdId });
    return r.members || [];
  },
  async adminHouseholdAddMember(householdId: number, userId: string, role: "owner" | "member" = "member"): Promise<void> {
    await rpc("admin.householdAddMember", { householdId, userId, role });
  },
  async adminHouseholdRemoveMember(householdId: number, userId: string): Promise<void> {
    await rpc("admin.householdRemoveMember", { householdId, userId });
  },
  async adminHouseholdRename(householdId: number, name: string): Promise<void> {
    await rpc("admin.householdRename", { householdId, name });
  },
  async adminHouseholdDelete(householdId: number): Promise<void> {
    await rpc("admin.householdDelete", { householdId });
  },
  async adminChoresList(householdId: number): Promise<Chore[]> {
    const r = await rpc<{ ok: true; chores: Chore[] }>("admin.choresList", { householdId });
    return r.chores || [];
  },
  async adminChoreCreate(args: {
    householdId: number;
    name: string;
    icon?: string | null;
    assignedToUserId?: string | null;
    frequency?: ChoreFrequency;
    stars?: number;
    requiresApproval?: boolean;
  }): Promise<number> {
    const r = await rpc<{ ok: true; id: number }>("admin.choreCreate", args);
    return r.id;
  },
  async adminChoreUpdate(choreId: number, patch: Partial<{
    name: string;
    icon: string | null;
    assignedToUserId: string | null;
    frequency: ChoreFrequency;
    stars: number;
    requiresApproval: boolean;
  }>): Promise<void> {
    await rpc("admin.choreUpdate", { choreId, patch });
  },
  async adminChoreArchive(choreId: number): Promise<void> {
    await rpc("admin.choreArchive", { choreId });
  },

  // --- Device diagnostic logs (v0.3.4) ---
  // The Android wrapper uploads its 250-line DiagLog ring buffer
  // via diag.uploadDeviceLog (any user can upload their own).
  // Admins list + read them via the admin.* namespace to debug
  // FCM registration / push delivery / etc. without asking the
  // user to paste from the on-device Diagnostics page.
  async diagUploadDeviceLog(args: { deviceId: string; versionName?: string | null; snapshot: string }): Promise<{ createdAt: string }> {
    const r = await rpc<{ ok: true; createdAt: string }>("diag.uploadDeviceLog", args);
    return { createdAt: r.createdAt };
  },
  async adminListDeviceLogs(limit = 50): Promise<DeviceLogSummary[]> {
    const r = await rpc<{ ok: true; logs: DeviceLogSummary[] }>("diag.listDeviceLogs", { limit });
    return r.logs || [];
  },
  async adminGetDeviceLog(id: number): Promise<DeviceLog> {
    const r = await rpc<{ ok: true; log: DeviceLog }>("diag.getDeviceLog", { id });
    return r.log;
  },

  // --- Chores (v51) ---
  // Catalog reads scope to the caller's active household. Completion
  // reads/writes additionally enforce a same-household check between
  // caller and target kid (see ChoresStore).
  async choresList(): Promise<Chore[]> {
    const r = await rpc<{ ok: true; chores: Chore[] }>("chores.list");
    return r.chores || [];
  },
  async choresListForKid(kidId: string, onDate?: string): Promise<KidChore[]> {
    const r = await rpc<{ ok: true; chores: KidChore[] }>("chores.listForKid", { kidId, onDate });
    return r.chores || [];
  },
  async choresCreate(input: {
    name: string;
    icon?: string | null;
    /** "single", "parallel", or "rotating". Defaults to "single".
     *  Rotating uses `assignees` as the ordered week-by-week roster. */
    assignmentMode?: "single" | "parallel" | "rotating";
    assignedToUserId?: string | null;
    /** Required for parallel + rotating modes — household user ids.
     *  Order matters for rotating (week N → assignees[N % length]). */
    assignees?: string[];
    frequency?: ChoreFrequency;
    rrule?: string | null;
    stars?: number;
    requiresApproval?: boolean;
  }): Promise<Chore> {
    const r = await rpc<{ ok: true; chore: Chore }>("chores.create", input);
    return r.chore;
  },
  async choresUpdate(choreId: number, patch: Partial<{
    name: string;
    icon: string | null;
    assignmentMode: "single" | "parallel" | "rotating";
    assignedToUserId: string | null;
    assignees: string[];
    frequency: ChoreFrequency;
    rrule: string | null;
    stars: number;
    requiresApproval: boolean;
  }>): Promise<Chore> {
    const r = await rpc<{ ok: true; chore: Chore }>("chores.update", { choreId, patch });
    return r.chore;
  },
  async choresArchive(choreId: number): Promise<void> {
    await rpc("chores.archive", { choreId });
  },
  async choresToggleDone(choreId: number, onDate?: string): Promise<ToggleDoneResult> {
    return await rpc<ToggleDoneResult>("chores.toggleDone", { choreId, onDate });
  },
  async choresApprove(completionId: number): Promise<{ ok: true; starsAwarded?: number; alreadyApproved?: boolean }> {
    return await rpc("chores.approve", { completionId });
  },
  async choresDeny(
    completionId: number,
    reason?: string,
  ): Promise<{ ok: true; alreadyDenied?: boolean }> {
    return await rpc("chores.deny", { completionId, reason: reason ?? null });
  },
  async choresPendingApprovals(): Promise<PendingApproval[]> {
    const r = await rpc<{ ok: true; pending: PendingApproval[] }>("chores.pendingApprovals");
    return r.pending || [];
  },
  async choresStarsThisWeek(kidId: string): Promise<number> {
    const r = await rpc<{ ok: true; stars: number }>("chores.starsThisWeek", { kidId });
    return r.stars || 0;
  },
  // Unified read for the All view — kid + adult chores merged into a
  // normalized shape. `today` is echoed so the client doesn't have
  // to recompute the server's TZ-aware today (avoids the UTC drift
  // bug we hit in MR 7a's Today view).
  async choresAllForHousehold(): Promise<{ chores: UnifiedChore[]; today: string }> {
    const r = await rpc<{ ok: true; chores: UnifiedChore[]; today: string }>("chores.allForHousehold");
    return { chores: r.chores || [], today: r.today };
  },

  // --- Adult chores (v54) — partner-share chart ---
  async adultChoresList(): Promise<AdultChore[]> {
    const r = await rpc<{ ok: true; chores: AdultChore[] }>("adultChores.list");
    return r.chores || [];
  },
  async adultChoresStarsThisWeek(): Promise<number> {
    const r = await rpc<{ ok: true; stars: number }>("adultChores.starsThisWeek");
    return r.stars || 0;
  },
  async adultChoresCreate(input: {
    name: string;
    icon?: string | null;
    category?: string | null;
    frequency?: AdultChoreFrequency;
    /** "single" | "rotating" | "parallel". Defaults to "single". */
    assignmentMode?: "single" | "rotating" | "parallel";
    /** Required for single mode (a user id). Ignored / inferred for
     *  rotating ("rotates") and parallel ("parallel" sentinel). */
    assignee?: string;
    /** Required for parallel mode — at least two user ids. */
    assignees?: string[];
    hue?: number;
    stars?: number;
  }): Promise<AdultChore> {
    const r = await rpc<{ ok: true; chore: AdultChore }>("adultChores.create", input);
    return r.chore;
  },
  async adultChoresUpdate(choreId: number, patch: Partial<{
    name: string;
    icon: string | null;
    category: string | null;
    frequency: AdultChoreFrequency;
    assignmentMode: "single" | "rotating" | "parallel";
    assignee: string;
    assignees: string[];
    hue: number;
    stars: number;
  }>): Promise<AdultChore> {
    const r = await rpc<{ ok: true; chore: AdultChore }>("adultChores.update", { choreId, patch });
    return r.chore;
  },
  /** Adult-only — grant or deduct bonus stars to a household member.
   *  Delta is a signed integer (positive grants, negative deducts).
   *  Optional reason gets stored on the audit row. Server returns the
   *  member's new running total. */
  async householdsAdjustStars(targetUserId: string, delta: number, reason?: string): Promise<number> {
    const r = await rpc<{ ok: true; total: number; delta: number }>(
      "households.adjustStars",
      { targetUserId, delta, reason },
    );
    return r.total;
  },

  // --- Bank: star → dollar conversion ---
  async bankRate(): Promise<number> {
    const r = await rpc<{ ok: true; rate: number }>("bank.rate");
    return r.rate;
  },
  async bankConfig(): Promise<{ rate: number; enabled: boolean }> {
    const r = await rpc<{ ok: true; rate: number; enabled: boolean }>("bank.config");
    return { rate: r.rate, enabled: r.enabled };
  },
  async bankSetRate(rate: number): Promise<void> {
    await rpc("bank.setRate", { rate });
  },
  async bankSetEnabled(enabled: boolean): Promise<void> {
    await rpc("bank.setEnabled", { enabled });
  },
  async bankRequest(starsAmount: number): Promise<{ requestId: number; stars: number; rate: number }> {
    return await rpc("bank.request", { starsAmount });
  },
  async bankApprove(requestId: number): Promise<{ ok: true; requestId: number; starsConsumed: number; dollarsCents: number }> {
    return await rpc("bank.approve", { requestId });
  },
  async bankDeny(requestId: number, reason?: string | null): Promise<void> {
    await rpc("bank.deny", { requestId, reason: reason || undefined });
  },
  async bankListPending(): Promise<BankPendingRequest[]> {
    const r = await rpc<{ ok: true; pending: BankPendingRequest[] }>("bank.listPending");
    return r.pending || [];
  },
  async bankMyPending(): Promise<BankMyPending | null> {
    const r = await rpc<{ ok: true; pending: BankMyPending | null }>("bank.myPending");
    return r.pending || null;
  },
  async bankLedger(targetUserId?: string, limit?: number): Promise<BankLedgerEntry[]> {
    const r = await rpc<{ ok: true; entries: BankLedgerEntry[] }>("bank.ledger", { targetUserId, limit });
    return r.entries || [];
  },
  async adultChoresDelete(choreId: number): Promise<void> {
    await rpc("adultChores.delete", { choreId });
  },
  async adultChoresMarkDone(choreId: number): Promise<{ ok: true; completedOn: string; nextDue: string; starsAwarded: number }> {
    return await rpc("adultChores.markDone", { choreId });
  },

  // --- Rewards (v55) ---
  async rewardsList(): Promise<Reward[]> {
    const r = await rpc<{ ok: true; rewards: Reward[] }>("rewards.list");
    return r.rewards || [];
  },
  async rewardsBalance(): Promise<{ earned: number; spent: number; balance: number }> {
    const r = await rpc<{ ok: true; earned: number; spent: number; balance: number }>("rewards.balance");
    return { earned: r.earned, spent: r.spent, balance: r.balance };
  },
  async rewardsCreate(input: {
    name: string;
    description?: string | null;
    icon?: string | null;
    costStars: number;
    requiresApproval?: boolean;
    maxPerWeek?: number | null;
  }): Promise<Reward> {
    const r = await rpc<{ ok: true; reward: Reward }>("rewards.create", input);
    return r.reward;
  },
  async rewardsUpdate(rewardId: number, patch: Partial<{
    name: string;
    description: string | null;
    icon: string | null;
    costStars: number;
    requiresApproval: boolean;
    maxPerWeek: number | null;
  }>): Promise<Reward> {
    const r = await rpc<{ ok: true; reward: Reward }>("rewards.update", { rewardId, patch });
    return r.reward;
  },
  async rewardsArchive(rewardId: number): Promise<void> {
    await rpc("rewards.archive", { rewardId });
  },
  async rewardsRedeem(rewardId: number, note?: string): Promise<{ ok: true; id: number; status: "pending" | "approved"; costStars: number; pendingApproval: boolean }> {
    return await rpc("rewards.redeem", { rewardId, note });
  },
  async rewardsApproveRedemption(redemptionId: number): Promise<void> {
    await rpc("rewards.approveRedemption", { redemptionId });
  },
  async rewardsDenyRedemption(redemptionId: number): Promise<void> {
    await rpc("rewards.denyRedemption", { redemptionId });
  },
  async rewardsPendingRedemptions(): Promise<PendingRedemption[]> {
    const r = await rpc<{ ok: true; pending: PendingRedemption[] }>("rewards.pendingRedemptions");
    return r.pending || [];
  },
  async rewardsMyRedemptions(limit = 20): Promise<MyRedemption[]> {
    const r = await rpc<{ ok: true; redemptions: MyRedemption[] }>("rewards.myRedemptions", { limit });
    return r.redemptions || [];
  },

  // --- Shopping ---
  async shoppingListLists(): Promise<ShoppingList[]> {
    const r = await rpc<{ ok: true; lists: ShoppingList[] }>("shopping.listLists");
    return r.lists || [];
  },
  async shoppingCreateList(name: string, isShared = false): Promise<ShoppingList> {
    const r = await rpc<{ ok: true; list: ShoppingList }>("shopping.createList", { name, isShared });
    return r.list;
  },
  async shoppingUpdateList(listId: number, patch: { name?: string; isShared?: boolean; sharedWithHousehold?: boolean; archived?: boolean }): Promise<ShoppingList> {
    const r = await rpc<{ ok: true; list: ShoppingList }>("shopping.updateList", { listId, ...patch });
    return r.list;
  },
  async shoppingDeleteList(listId: number): Promise<void> {
    await rpc("shopping.deleteList", { listId });
  },
  async shoppingListShares(listId: number): Promise<Array<{ userId: string; createdAt: string }>> {
    const r = await rpc<{ ok: true; shares: Array<{ userId: string; createdAt: string }> }>("shopping.listShares", { listId });
    return r.shares || [];
  },
  async shoppingShareWith(listId: number, targetUserId: string): Promise<void> {
    await rpc("shopping.shareWith", { listId, targetUserId });
  },
  async shoppingUnshareWith(listId: number, targetUserId: string): Promise<void> {
    await rpc("shopping.unshareWith", { listId, targetUserId });
  },
  async shoppingListShareableUsers(): Promise<Array<{ id: string }>> {
    const r = await rpc<{ ok: true; users: Array<{ id: string }> }>("shopping.listShareableUsers");
    return r.users || [];
  },
  async shoppingListItems(listId: number): Promise<ShoppingItem[]> {
    const r = await rpc<{ ok: true; items: ShoppingItem[] }>("shopping.listItems", { listId });
    return r.items || [];
  },
  async shoppingAddItem(listId: number, text: string, quantity?: string): Promise<ShoppingItem> {
    const r = await rpc<{ ok: true; item: ShoppingItem }>("shopping.addItem", { listId, text, quantity });
    return r.item;
  },
  async shoppingUpdateItem(itemId: number, patch: Partial<ShoppingItem>): Promise<ShoppingItem> {
    const r = await rpc<{ ok: true; item: ShoppingItem }>("shopping.updateItem", { itemId, ...patch });
    return r.item;
  },
  async shoppingDeleteItem(itemId: number): Promise<void> {
    await rpc("shopping.deleteItem", { itemId });
  },
  async shoppingClearPurchased(listId: number): Promise<number> {
    const r = await rpc<{ ok: true; deleted: number }>("shopping.clearPurchased", { listId });
    return r.deleted || 0;
  },
  async shoppingPlan(args: {
    listId: number;
    mode: ShoppingPlanMode;
    query?: string;
    recipeIds?: string[];
  }): Promise<{ proposals: ShoppingProposal[]; mode: ShoppingPlanMode }> {
    const r = await rpc<{ ok: true; proposals: ShoppingProposal[]; mode: ShoppingPlanMode }>("shopping.plan", args);
    return { proposals: r.proposals || [], mode: r.mode };
  },

  // --- Recipes ---
  async recipesList(): Promise<Recipe[]> {
    const r = await rpc<{ ok: true; recipes: Recipe[] }>("recipes.list");
    return r.recipes || [];
  },
  async recipesGet(recipeId: string): Promise<{ recipe: Recipe; items: RecipeItem[]; owned: boolean }> {
    const r = await rpc<any>("recipes.get", { recipeId });
    return { recipe: r.recipe, items: r.items || [], owned: !!r.owned };
  },
  /** Browse the global read-only catalogue (anonymized + `owned` flag). */
  async recipesCatalogue(opts: { search?: string; limit?: number } = {}): Promise<CatalogueRecipe[]> {
    const r = await rpc<{ ok: true; recipes: CatalogueRecipe[] }>("recipes.catalogue", {
      search: opts.search || undefined,
      limit: opts.limit,
    });
    return r.recipes || [];
  },
  /** Copy a catalogue recipe into our household's box (one per household). */
  async recipesImportFromCatalogue(catalogueId: number): Promise<Recipe> {
    const r = await rpc<{ ok: true; recipe: Recipe }>("recipes.importFromCatalogue", { catalogueId });
    return r.recipe;
  },
  async recipesCreate(name: string): Promise<Recipe> {
    const r = await rpc<{ ok: true; recipe: Recipe }>("recipes.create", { name });
    return r.recipe;
  },
  async recipesUpdate(recipeId: string, patch: Partial<Pick<Recipe, "name" | "imageUrl" | "instructions" | "notes" | "serves">>): Promise<Recipe> {
    const r = await rpc<{ ok: true; recipe: Recipe }>("recipes.update", { recipeId, ...patch });
    return r.recipe;
  },
  async recipesDelete(recipeId: string): Promise<void> {
    await rpc("recipes.delete", { recipeId });
  },
  async recipesSetTags(recipeId: string, tags: string[]): Promise<Recipe> {
    const r = await rpc<{ ok: true; recipe: Recipe }>("recipes.setTags", { recipeId, tags });
    return r.recipe;
  },
  async recipesRetag(recipeId: string): Promise<void> {
    await rpc("recipes.retag", { recipeId });
  },
  async recipesAddToList(
    recipeId: string,
    listId: number,
    opts: { scale?: number; usePantry?: boolean; includeSeasonings?: string[] } = {},
  ): Promise<RecipeAddToListResult> {
    const r = await rpc<{ ok: true } & RecipeAddToListResult>("recipes.addToList", {
      recipeId, listId, scale: opts.scale ?? 1,
      // Default to gating against pantry; client passes false on the
      // "add anyway" override path.
      usePantry: opts.usePantry !== false,
      // Canonical seasoning names the user opted to buy. Omitted/empty =
      // no seasonings added (they're never auto-added).
      includeSeasonings: opts.includeSeasonings ?? [],
    });
    return {
      added: r.added,
      skipped: r.skipped,
      pantrySkipped: r.pantrySkipped || [],
      recipeName: r.recipeName,
      listName: r.listName,
      seasoningsAdded: r.seasoningsAdded,
    };
  },
  /** Seasonings a recipe calls for + whether the household stocks each.
   *  Non-mutating — drives the add-to-list seasonings picker. */
  async recipesSeasoningsFor(recipeId: string): Promise<{ configured: boolean; seasonings: RecipeSeasoning[] }> {
    const r = await rpc<{ ok: true; configured: boolean; seasonings: RecipeSeasoning[] }>(
      "recipes.seasoningsFor", { recipeId },
    );
    return { configured: !!r.configured, seasonings: r.seasonings || [] };
  },
  async recipesAddItem(recipeId: string, text: string, quantity?: string): Promise<RecipeItem> {
    const r = await rpc<{ ok: true; item: RecipeItem }>("recipes.addItem", { recipeId, text, quantity });
    return r.item;
  },
  async recipesUpdateItem(itemId: string, patch: Partial<RecipeItem>): Promise<RecipeItem> {
    const r = await rpc<{ ok: true; item: RecipeItem }>("recipes.updateItem", { itemId, ...patch });
    return r.item;
  },
  async recipesDeleteItem(itemId: string): Promise<void> {
    await rpc("recipes.deleteItem", { itemId });
  },
  async recipesSuggestions(limit?: number): Promise<RecipeSuggestion[]> {
    const r = await rpc<{ ok: true; suggestions: RecipeSuggestion[] }>("recipes.suggestions", limit != null ? { limit } : {});
    return r.suggestions || [];
  },

  // --- Notes ---
  async notesListRoots(): Promise<NotesRoot[]> {
    const r = await rpc<{ ok: true; roots: NotesRoot[] }>("notes.listRoots");
    return r.roots || [];
  },
  async notesListDir(root: string, path = ""): Promise<NotesEntry[]> {
    const r = await rpc<{ ok: true; entries: NotesEntry[] }>("notes.listDir", { root, path });
    return r.entries || [];
  },
  async notesRead(root: string, path: string): Promise<NotesNote> {
    const r = await rpc<NotesNote & { ok: true }>("notes.read", { root, path });
    return r as unknown as NotesNote;
  },
  async notesSave(args: { root: string; path: string; content: string; expectedSha?: string }): Promise<{
    ok: boolean;
    sha256?: string;
    size?: number;
    mtime?: number;
    error?: string;
    // Optimistic-locking conflict payload from notes_storage.saveNote
    currentSha?: string;
    currentContent?: string;
    // Reindex status echo
    reindexed?: boolean;
    chunks?: number;
    pending?: boolean;
    reindexError?: string;
  }> {
    // Use rpcRaw — on ok:false the server returns `currentSha` and
    // `currentContent` (the conflict payload). The default rpc() would
    // throw and lose those fields, so the editor wouldn't know how to
    // render the merge banner.
    return await rpcRaw<any>("notes.save", args);
  },
  async notesListTemplates(root: string): Promise<NotesTemplate[]> {
    const r = await rpc<{ ok: true; templates: NotesTemplate[] }>("notes.listTemplates", { root });
    return r.templates || [];
  },
  async notesDelete(root: string, path: string): Promise<void> {
    await rpc("notes.delete", { root, path });
  },
  async notesRename(args: { root: string; oldPath: string; newPath: string; expectedSha?: string }): Promise<{ ok: boolean; newPath?: string; sha256?: string | null; error?: string }> {
    const r = await rpc<any>("notes.rename", args);
    return r;
  },
  // Upload a binary asset (today: pasted/dropped images) under
  // `<root>/_assets/`. dataUrl must be `data:<mime>;base64,...`. Server
  // returns the relative path the markdown body should use plus a GET
  // URL the renderer can hit directly.
  async notesUploadAsset(args: { root: string; dataUrl: string }): Promise<{ ok: boolean; relPath?: string; url?: string; size?: number; mime?: string; error?: string }> {
    const r = await rpc<any>("notes.uploadAsset", args);
    return r;
  },
  async notesSearch(root: string, query: string, limit = 25): Promise<NotesSearchHit[]> {
    const r = await rpc<{ ok: true; hits: NotesSearchHit[] }>("notes.search", { root, query, limit });
    return r.hits || [];
  },
  async notesBacklinks(root: string, path: string): Promise<NotesBacklink[]> {
    const r = await rpc<{ ok: true; hits: NotesBacklink[] }>("notes.backlinks", { root, path });
    return r.hits || [];
  },

  // --- Calendar (Phase 1: personal events, no recurring/sharing/invites) ---
  async calendarListEvents(args: { rangeStart: string; rangeEnd: string }): Promise<CalendarEvent[]> {
    const r = await rpc<{ ok: true; events: CalendarEvent[] }>("calendar.listEvents", args);
    return r.events || [];
  },
  async calendarCreateEvent(args: CalendarCreateArgs): Promise<CalendarEvent> {
    const r = await rpc<{ ok: true; event: CalendarEvent }>("calendar.createEvent", args as unknown as Record<string, unknown>);
    return r.event;
  },
  async calendarUpdateEvent(args: { id: string; patch: CalendarUpdatePatch }): Promise<CalendarEvent> {
    const r = await rpc<{ ok: true; event: CalendarEvent }>("calendar.updateEvent", args);
    return r.event;
  },
  async calendarDeleteEvent(id: string): Promise<void> {
    await rpc("calendar.deleteEvent", { id });
  },
  async calendarListReminders(eventId: string): Promise<number[]> {
    const r = await rpc<{ ok: true; reminders: number[] }>("calendar.listReminders", { eventId });
    return r.reminders || [];
  },

  // --- Shared calendars (Phase 2A) ---
  async calendarListMyCalendars(): Promise<Calendar[]> {
    const r = await rpc<{ ok: true; calendars: Calendar[] }>("calendar.listMyCalendars");
    return r.calendars || [];
  },
  async calendarCreateCalendar(args: { name: string; color?: string | null }): Promise<Calendar> {
    const r = await rpc<{ ok: true; calendar: Calendar }>("calendar.createCalendar", args);
    return r.calendar;
  },
  async calendarRenameCalendar(args: { id: string; name: string }): Promise<Calendar> {
    const r = await rpc<{ ok: true; calendar: Calendar }>("calendar.renameCalendar", args);
    return r.calendar;
  },
  async calendarDeleteCalendar(id: string): Promise<void> {
    await rpc("calendar.deleteCalendar", { id });
  },
  // v53 — flag a calendar as the household calendar for a household
  // (or pass householdId: null to clear). Kids in that household see
  // every event on it.
  async calendarSetHousehold(args: { id: string; householdId: number | null }): Promise<Calendar> {
    const r = await rpc<{ ok: true; calendar: Calendar }>("calendar.setHousehold", args);
    return r.calendar;
  },
  async calendarListMembers(id: string): Promise<CalendarMember[]> {
    const r = await rpc<{ ok: true; members: CalendarMember[] }>("calendar.listMembers", { id });
    return r.members || [];
  },
  async calendarAddMember(args: { id: string; targetUserId: string; role?: "owner" | "editor" | "viewer" }): Promise<void> {
    await rpc("calendar.addMember", args);
  },
  async calendarRemoveMember(args: { id: string; targetUserId: string }): Promise<void> {
    await rpc("calendar.removeMember", args);
  },
  async calendarListShareableUsers(): Promise<CalendarShareableUser[]> {
    const r = await rpc<{ ok: true; users: CalendarShareableUser[] }>("calendar.listShareableUsers");
    return r.users || [];
  },

  // --- Event invites (Phase 2B) ---
  async calendarListInvitees(eventId: string): Promise<CalendarInvitee[]> {
    const r = await rpc<{ ok: true; invitees: CalendarInvitee[] }>("calendar.listInvitees", { eventId });
    return r.invitees || [];
  },
  async calendarInviteUser(args: { eventId: string; targetUserId: string }): Promise<void> {
    await rpc("calendar.inviteUser", args);
  },
  async calendarUninviteUser(args: { eventId: string; targetUserId: string }): Promise<void> {
    await rpc("calendar.uninviteUser", args);
  },
  async calendarRespondToInvite(args: { eventId: string; status: "accepted" | "declined" | "pending" }): Promise<void> {
    await rpc("calendar.respondToInvite", args);
  },

  // --- Image generation (Studio) ---
  async imageGenListOptions(): Promise<ImageGenOptions> {
    const r = await rpc<ImageGenOptions & { ok: true }>("imageGen.listOptions");
    return { quality: r.quality, style: r.style, aspect: r.aspect, defaults: r.defaults };
  },
  async imageGenGetSettings(): Promise<ImageGenPrefs> {
    const r = await rpc<{ ok: true; prefs: ImageGenPrefs }>("imageGen.getSettings");
    return r.prefs;
  },
  async imageGenUpdateSettings(args: ImageGenPrefs): Promise<void> {
    await rpc("imageGen.updateSettings", args as unknown as Record<string, unknown>);
  },
  async imageGenGenerate(args: ImageGenGenerateArgs): Promise<ImageGenGenerateResult> {
    const r = await rpc<{ ok: true } & ImageGenGenerateResult>("imageGen.generate", args as unknown as Record<string, unknown>);
    return { galleryId: r.galleryId, galleryIds: r.galleryIds || [] };
  },
  async galleryList(args: { limit?: number; offset?: number; query?: string } = {}): Promise<GalleryItem[]> {
    const r = await rpc<{ ok: true; items: GalleryItem[] }>("gallery.list", args);
    return r.items || [];
  },
  async galleryGet(id: number): Promise<GalleryItem | null> {
    try {
      const r = await rpc<{ ok: true; item: GalleryItem }>("gallery.get", { id });
      return r.item;
    } catch { return null; }
  },
  async galleryDelete(id: number): Promise<void> {
    await rpc("gallery.delete", { id });
  },

  // --- Settings Phase 2: TOTP / 2FA ---
  async totpStatus(): Promise<{ enabled: boolean; setupPending: boolean; recoveryCodesRemaining: number }> {
    return await rpc("totp.status");
  },
  async totpSetup(): Promise<{ secret: string; otpauthUrl: string }> {
    return await rpc("totp.setup");
  },
  async totpEnable(code: string): Promise<{ recoveryCodes: string[] }> {
    return await rpc("totp.enable", { code });
  },
  async totpRegenerateRecoveryCodes(code: string): Promise<{ recoveryCodes: string[] }> {
    return await rpc("totp.regenerateRecoveryCodes", { code });
  },
  async totpDisable(code: string): Promise<void> {
    await rpc("totp.disable", { code });
  },

  // --- Alarms (safety-net view in Settings) ---
  // Read + cancel only — creation lives in the chat (set_alarm tool) or
  // on Android via the native VultureAlarms numpad. The settings list is
  // the "let me see what's actually set and kill the one that's haywire"
  // failsafe.
  async alarmsList(): Promise<Alarm[]> {
    const r = await rpc<{ ok: true; alarms: Alarm[] }>("alarms.list");
    return r.alarms || [];
  },
  async alarmsCancel(id: string): Promise<boolean> {
    const r = await rpc<{ ok: boolean; removed?: boolean }>("alarms.cancel", { id });
    return !!r.removed;
  },
  // One-off timer from a UI button — cook-mode step timers, "Add 5min
  // pasta timer" buttons, etc. Server picks the id if you don't.
  async alarmsSet(args: { name: string; fireAt: number; body?: string; kind?: "once" | "daily"; atHHMM?: string }): Promise<Alarm> {
    const r = await rpc<{ ok: true; alarm: Alarm }>("alarms.set", { kind: "once", ...args });
    return r.alarm;
  },

  // --- Settings Phase 2: Device tokens ---
  // The `admin.*` namespace name is historical — these endpoints allow
  // self-management without admin gating when targetId === user.id.
  async deviceTokenList(): Promise<DeviceToken[]> {
    const r = await rpc<{ ok: true; tokens: DeviceToken[] }>("admin.listDeviceTokens");
    return r.tokens || [];
  },
  async deviceTokenIssue(name: string): Promise<{ id: string; name: string; token: string }> {
    return await rpc("admin.issueDeviceToken", { name });
  },
  async deviceTokenRevoke(tokenId: string): Promise<void> {
    await rpc("admin.revokeDeviceToken", { tokenId });
  },

  // --- Settings Phase 2: Secrets ---
  async secretsList(): Promise<SecretRef[]> {
    const r = await rpc<{ ok: true; secrets: SecretRef[] }>("secrets.list");
    return r.secrets || [];
  },
  async secretsSet(name: string, value: string): Promise<void> {
    await rpc("secrets.set", { name, value });
  },
  async secretsDelete(name: string): Promise<void> {
    await rpc("secrets.delete", { name });
  },

  // --- Admin (admin-only — server enforces) ---
  async adminListUsers(): Promise<AdminUser[]> {
    const r = await rpc<{ ok: true; users: AdminUser[] }>("admin.listUsers");
    return r.users || [];
  },
  async adminCreateUser(args: { id: string; password: string; isAdmin?: boolean; email?: string }): Promise<AdminCreateUserResult> {
    const r = await rpc<{ ok: true; user: AdminUser; email: string | null; auth0?: AdminAuth0Status }>(
      "admin.createUser", args,
    );
    return { user: r.user, email: r.email, auth0: r.auth0 || null };
  },
  async adminProvisionAuth0(id: string): Promise<{ created: boolean; resetEmailSent: boolean; email: string }> {
    return await rpc("admin.provisionAuth0", { id });
  },
  async adminDeleteUser(id: string): Promise<void> {
    await rpc("admin.deleteUser", { id });
  },
  async adminSetUserEmail(id: string, email: string): Promise<void> {
    await rpc("admin.setUserEmail", { id, email });
  },
  async adminSetForceTotp(id: string, enabled: boolean): Promise<void> {
    await rpc("admin.setForceTotp", { id, enabled });
  },
  async adminAuditLog(args: { limit?: number; actor?: string; action?: string; sinceDays?: number } = {}): Promise<AuditEntry[]> {
    const r = await rpc<{ ok: true; entries: AuditEntry[] }>("admin.auditLog", args);
    return r.entries || [];
  },
  async adminAuditVerify(): Promise<AuditVerifyResult> {
    return await rpc<AuditVerifyResult>("admin.auditVerify");
  },
  async adminListSessions(): Promise<AdminChatSession[]> {
    const r = await rpc<{ ok: true; sessions: AdminChatSession[] }>("admin.listSessions");
    return r.sessions || [];
  },
  async adminUsageStats(args: { sinceDays?: number; userId?: string } = {}): Promise<UsageStats> {
    const r = await rpc<{ ok: true; stats: UsageStats }>("admin.usageStats", args);
    return r.stats;
  },
  async adminLlmTargets(): Promise<LlmTarget[]> {
    const r = await rpc<{ ok: true; targets: LlmTarget[] }>("admin.llmTargets");
    return r.targets || [];
  },
  async adminLlmStatus(): Promise<LlmStatusReport> {
    const r = await rpc<{ ok: true; status: LlmStatusReport }>("admin.llmStatus");
    return r.status;
  },
  async adminLlmSwitch(target: string): Promise<void> {
    await rpc("admin.llmSwitch", { target });
  },
  async adminBackupsList(): Promise<{ backups: AdminBackup[]; lastBackupAt: string | null; dataDir: string }> {
    return await rpc("admin.backupsList");
  },
  async adminBackupNow(): Promise<{ path: string; lastBackupAt: string | null }> {
    return await rpc("admin.backupNow");
  },
  async imageGenInpaint(args: ImageGenInpaintArgs): Promise<ImageGenInpaintResult> {
    const r = await rpc<{ ok: true } & ImageGenInpaintResult>(
      "imageGen.inpaint", args as unknown as Record<string, unknown>,
    );
    return {
      galleryId: r.galleryId ?? null,
      filename: r.filename,
      workspaceRelativePath: r.workspaceRelativePath,
      elapsedMs: r.elapsedMs,
    };
  },

  // --- Meal plans ---
  async mealplanListForRange(from: string, to: string): Promise<MealPlanEntry[]> {
    const r = await rpc<{ ok: true; entries: MealPlanEntry[] }>("mealplan.listForRange", { from, to });
    return r.entries || [];
  },
  async mealplanSet(args: { date: string; slot: MealSlot; value: string; recipeId?: string | null }): Promise<MealPlanEntry> {
    const r = await rpc<{ ok: true; entry: MealPlanEntry }>("mealplan.set", args);
    return r.entry;
  },
  async mealplanClear(args: { date: string; slot: MealSlot }): Promise<boolean> {
    const r = await rpc<{ ok: true; removed: boolean }>("mealplan.clear", args);
    return !!r.removed;
  },
  // Meal-plan substitutions — per-day overlays on a recipe's
  // ingredient list. See src/meal_plan_overlay.mjs for op shapes.
  async mealplanSubstitute(args: {
    date: string;
    slot: MealSlot;
    op:
      | { op: "swap"; recipeItemId: number; originalText: string; substituteText: string; substituteQty?: string | null; note?: string | null }
      | { op: "remove"; recipeItemId: number; originalText: string }
      | { op: "add"; text: string; quantity?: string | null }
      | { op: "unswap"; recipeItemId: number }
      | { op: "unremove"; recipeItemId: number }
      | { op: "deleteAddition"; id: string }
      | { op: "clear" };
  }): Promise<MealPlanEntry> {
    const r = await rpc<{ ok: true; entry: MealPlanEntry }>("mealplan.substitute", args);
    return r.entry;
  },
  async mealplanGetEffectiveItems(args: { date: string; slot: MealSlot }): Promise<{ entry: MealPlanEntry; items: EffectiveMealItem[] }> {
    const r = await rpc<{ ok: true; entry: MealPlanEntry; items: EffectiveMealItem[] }>("mealplan.getEffectiveItems", args);
    return { entry: r.entry, items: r.items || [] };
  },
  async mealplanSuggestSubstitutes(args: {
    recipeId: string | number;
    recipeItemId: number;
    userHint?: string;
  }): Promise<SubstitutionSuggestion[]> {
    const r = await rpc<{ ok: true; suggestions: SubstitutionSuggestion[] }>("mealplan.suggestSubstitutes", args);
    return r.suggestions || [];
  },
  async mealplanAi(args: {
    weekStart: string;
    constraints: string;
    slots: { breakfast: boolean; lunch: boolean; dinner: boolean };
    currentPlan?: Array<{ day: number; slot: MealSlot; value: string }>;
  }): Promise<{ proposals: MealProposalRow[]; weekStart: string; dropped: number }> {
    const r = await rpc<{ ok: true; proposals: MealProposalRow[]; weekStart: string; dropped?: number }>("mealplan.ai", args);
    return { proposals: r.proposals || [], weekStart: r.weekStart, dropped: r.dropped || 0 };
  },

  // Preview-only: extracts ingredients + steps from a URL or pasted
  // text. Does NOT save. Caller reviews + edits, then commits via
  // recipesCreateWithItems.
  async recipesImport(args: { url?: string; text?: string }): Promise<RecipeImportPreview> {
    const r = await rpc<any>("recipes.import", args);
    return {
      items: r.items || [],
      suggestedName: r.suggestedName || "",
      imageUrl: r.imageUrl || null,
      instructions: r.instructions || [],
      serves: typeof r.serves === "number" ? r.serves : null,
      source: r.source || "",
      sourceUrl: r.sourceUrl || null,
      existing: r.existing || null,
    };
  },
  async recipesCreateWithItems(args: {
    name: string;
    items: Array<{ text: string; quantity?: string }>;
    imageUrl?: string | null;
    instructions?: string[] | null;
    serves?: number | null;
    sourceUrl?: string | null;
  }): Promise<{ recipe: Recipe; itemCount: number }> {
    const r = await rpc<any>("recipes.createWithItems", args);
    return { recipe: r.recipe, itemCount: r.itemCount || 0 };
  },

  // --- User profile (household size etc.) ---
  async userGetProfile(): Promise<{ householdSize: number | null }> {
    const r = await rpc<{ ok: true; profile: { householdSize: number | null } }>("user.getProfile");
    return r.profile;
  },
  async userSetHouseholdSize(size: number | null): Promise<number | null> {
    const r = await rpc<{ ok: true; householdSize: number | null }>("user.setHouseholdSize", { householdSize: size });
    return r.householdSize;
  },

  // --- Kids LLM chat (parent controls + review) ---
  async kidsList(): Promise<KidSummary[]> {
    const r = await rpc<{ ok: true; kids: KidSummary[] }>("kids.list");
    return r.kids || [];
  },
  async kidsGetSettings(kidUserId: string): Promise<KidChatSettings> {
    const r = await rpc<{ ok: true; settings: KidChatSettings }>("kids.getSettings", { kidUserId });
    return r.settings;
  },
  async kidsSetSettings(kidUserId: string, patch: Partial<KidChatSettings>): Promise<KidChatSettings> {
    const r = await rpc<{ ok: true; settings: KidChatSettings }>("kids.setSettings", { kidUserId, ...patch });
    return r.settings;
  },
  async kidsListChats(kidUserId: string): Promise<KidChatSessionSummary[]> {
    const r = await rpc<{ ok: true; sessions: KidChatSessionSummary[] }>("kids.listChats", { kidUserId });
    return r.sessions || [];
  },
  async kidsLoadChat(kidUserId: string, sessionId: string): Promise<{ messages: SessionMessage[]; name?: string } | null> {
    const r = await rpc<{ ok: true; session: any }>("kids.loadChat", { kidUserId, sessionId });
    if (!r.session) return null;
    const raw = r.session.messages;
    const messages = Array.isArray(raw) ? raw : (typeof raw === "string" ? safeParseArray(raw) : []);
    return { messages, name: r.session.name };
  },
  async kidsListFlags(kidUserId: string): Promise<KidChatFlag[]> {
    const r = await rpc<{ ok: true; flags: KidChatFlag[] }>("kids.listFlags", { kidUserId });
    return r.flags || [];
  },
  async kidsReviewFlag(flagId: number): Promise<void> {
    await rpc("kids.reviewFlag", { flagId });
  },
  /** Kid-facing: is chat allowed right now? `enabled` is the parent
   *  permission (controls whether the Chat tab shows at all); `allowed` also
   *  factors temporary gates (quiet hours / daily limit). */
  async kidchatStatus(): Promise<{ enabled: boolean; allowed: boolean; reason: string | null }> {
    const r = await rpc<{ ok: true; enabled: boolean; allowed: boolean; reason: string | null }>("kidchat.status");
    return { enabled: !!r.enabled, allowed: !!r.allowed, reason: r.reason ?? null };
  },
  /** Toggle a KidApp surface (rewards/calendar/todo/journal) for a kid. */
  async kidsSetFeature(kidUserId: string, feature: string, enabled: boolean): Promise<void> {
    await rpc("kids.setFeature", { kidUserId, feature, enabled });
  },
  /** Kid-facing: which surfaces this kid currently gets (KidApp tab gating). */
  async kidSurfaces(): Promise<Record<string, boolean>> {
    const r = await rpc<{ ok: true; surfaces: Record<string, boolean> }>("kid.surfaces");
    return r.surfaces || {};
  },
};

function safeParseArray(s: string): any[] {
  try { const v = JSON.parse(s); return Array.isArray(v) ? v : []; } catch { return []; }
}

/** Per-kid chat controls a parent sets. */
export interface KidChatSettings {
  enabled: boolean;
  /** Messages/day cap; null = unlimited. */
  dailyLimit: number | null;
  /** Local "HH:MM" quiet-hours window; null = none. */
  quietStart: string | null;
  quietEnd: string | null;
  /** Assistant style tuning for this kid; null = default. */
  persona: string | null;
}

/** Per-kid KidApp surface gates (Today + Chores are always on, not
 *  here). `calendar` was removed when the dedicated Calendar tab was
 *  collapsed onto Today — schedule events render unconditionally now. */
export interface KidFeatureFlags {
  rewards: boolean;
  todo: boolean;
  journal: boolean;
}

/** A kid + their chat status, for the parent "Family" cards. */
export interface KidSummary {
  kidUserId: string;
  settings: KidChatSettings;
  features: KidFeatureFlags;
  usageToday: number;
  unreviewedFlags: number;
}

export interface KidChatSessionSummary {
  id: string;
  name: string;
  updatedAt?: string;
  updated_at?: string;
}

export interface KidChatFlag {
  id: number;
  sessionId: string | null;
  seq: number | null;
  role: string | null;
  categories: string[];
  snippet: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

export interface RecipeImportPreview {
  items: Array<{ text: string; quantity: string; category?: string | null; emoji?: string | null }>;
  suggestedName: string;
  imageUrl: string | null;
  instructions: string[];
  serves: number | null;
  source: string;
  /** Origin URL echoed back so the commit (createWithItems) can store it
   *  for future catalogue dedup. Null for the pasted-text path. */
  sourceUrl: string | null;
  /** Set when this URL is already a catalogue master — the UI can offer to
   *  add the existing one instead of saving a duplicate. */
  existing: { catalogueId: number; name: string; owned: boolean } | null;
}

export interface TodoList {
  id: string;
  name: string;
  archived?: boolean;
  createdAt?: string;
}

// --- Notes ---
export interface NotesRoot {
  id: string;
  /** Server returns this as `label`. Keeping the name `label` lines
   *  the type up with the wire shape — don't rename without also
   *  fixing the server. */
  label: string;
  writable: boolean;
  scope?: "user" | "global" | "owned";
  color?: string;
}
export interface NotesEntry {
  name: string;
  type: "file" | "dir";
  size?: number;
  mtimeMs?: number;
  sha256?: string;
}
export interface NotesNote {
  content: string;
  sha256?: string;
  mtimeMs?: number;
  kind?: string;
}
export interface NotesSearchHit {
  path: string;
  snippet?: string;
  score?: number;
}
export interface NotesTemplate {
  name: string;
  path: string;
}
export interface NotesBacklink {
  path: string;
  title: string;
  snippet: string;
  count: number;
}

// --- Calendar ---
//
// Server wire shape from calendar_store.mjs. Phase 1 of the port only
// touches personal (no calendarId) events, so the share/invite/recurrence
// fields are surfaced but ignored by the UI.
export interface CalendarEvent {
  id: string;
  ownerUserId: string;
  calendarId: string | null;
  masterId: string | null;
  originalDtstart: string | null;
  title: string;
  description: string;
  location: string;
  dtstart: string;
  dtend: string;
  allDay: boolean;
  private: boolean;
  rrule: string | null;
  exdate: string;
  isRecurring: boolean;
  createdAt: string;
  updatedAt: string;
  reminderCount?: number;
  myInviteStatus?: string | null;
  /** Per-user event tag (v52). NULL = household-wide; a set user id
   *  pins the event to that one user. Used by kid Calendar to filter
   *  to events that are either household-wide OR tagged for them. */
  forUserId?: string | null;
  /** Client-only — see ShoppingItem._queued for details. */
  _queued?: boolean;
}

export interface CalendarCreateArgs {
  title: string;
  dtstart: string;
  dtend: string;
  allDay?: boolean;
  description?: string;
  location?: string;
  calendarId?: string | null;
  /** Minutes-before-start offsets the user wants to be reminded at.
   *  E.g. `[15, 1440]` = "15 min before" + "1 day before". Server
   *  materializes alarms via the central alarms table on save. */
  reminders?: number[];
  /** Optional RFC 5545 RRULE (without the "RRULE:" prefix) for recurring
   *  events. The store expands it into instances at listEvents time via
   *  rrule.js; one row holds the master + recurrence rule. */
  rrule?: string | null;
  /** Pin the event to one household member (typically a kid) so it
   *  shows up in that kid's calendar with a highlight. Pass `null`
   *  or omit for a household-wide event. */
  forUserId?: string | null;
}

export interface CalendarUpdatePatch {
  title?: string;
  description?: string;
  location?: string;
  dtstart?: string;
  dtend?: string;
  allDay?: boolean;
  calendarId?: string | null;
  /** Replaces the full reminder set when present. Omit to leave
   *  reminders untouched; pass `[]` to clear them. */
  reminders?: number[];
  /** Replaces the RFC 5545 RRULE on the master row. Pass `null` to
   *  remove recurrence (turns a recurring event into a one-off);
   *  omit to leave the rule untouched. */
  rrule?: string | null;
  /** Replaces the per-user tag. `null` clears it (back to household-
   *  wide); omit to leave unchanged. */
  forUserId?: string | null;
}

export interface Calendar {
  id: string;
  name: string;
  color: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  myRole: "owner" | "editor" | "viewer";
  /** v53 — when set, kids in this household see every event on the
   *  calendar (the kid-scope listEvents handler matches on this). */
  householdId?: number | null;
}

export interface CalendarMember {
  userId: string;
  role: "owner" | "editor" | "viewer";
  joinedAt: string;
}

export interface CalendarShareableUser {
  id: string;
}

export interface CalendarInvitee {
  userId: string;
  status: "pending" | "accepted" | "declined";
  invitedAt: string;
  respondedAt: string | null;
}

// --- Image generation (Studio) ---
//
// `listOptions` enumerates the server-side QUALITY/STYLE/ASPECT presets
// the user can pick from. Defaults track the per-user prefs (saved via
// updateSettings) or fall back to server defaults.
export interface ImageGenOptionEnum {
  label: string;
  description: string | null;
}
export interface ImageGenOptions {
  quality: Record<string, ImageGenOptionEnum>;
  style: Record<string, ImageGenOptionEnum>;
  aspect: Record<string, ImageGenOptionEnum>;
  defaults: { quality: string; style: string; aspect: string };
}
export interface ImageGenPrefs {
  quality: string;
  style: string;
  aspect: string;
  advanced?: Record<string, unknown> | null;
}
export interface ImageGenGenerateArgs {
  prompt: string;
  style?: string;
  quality?: string;
  aspect?: string;
  candidates?: number;
  seed?: number;
  negative_extra?: string;
  negative_strip?: string;
  /** SSE channel id — the client picks one (typically a uuid) and
   *  subscribes to /api/events?session=<id> in parallel to receive
   *  per-candidate progress + preview frames during generation. */
  sseSessionId?: string;
  /** Reference image as `data:image/...;base64,...`. 16MB cap on the
   *  server. Drives both the Redux IP-Adapter (when useRedux) and
   *  Canny/ControlNet (when useCannyLock). */
  referenceImageDataUrl?: string;
  refStrength?: number;
  useRedux?: boolean;
  useCannyLock?: boolean;
  cannyStrength?: number;
  /** One of: "flux_controlnet_canny_instantx.safetensors" |
   *  "flux_controlnet_union_pro2.safetensors". Anything else is
   *  silently coerced to the InstantX default. */
  controlnetModel?: string;
}

export type ControlNetModel =
  | "flux_controlnet_canny_instantx.safetensors"
  | "flux_controlnet_union_pro2.safetensors";

export const CONTROLNET_MODELS: { id: ControlNetModel; label: string; description: string }[] = [
  { id: "flux_controlnet_canny_instantx.safetensors", label: "InstantX", description: "Default Canny lock — softer adherence to edges." },
  { id: "flux_controlnet_union_pro2.safetensors",     label: "Union Pro v2", description: "Stronger structural lock; better for tight shape carryover." },
];

export interface ImageGenInpaintArgs {
  sourcePath: string;
  sourceWidth: number;
  sourceHeight: number;
  /** Base64 PNG mask at SOURCE dimensions. White = paint area, the
   *  rest = keep. The server accepts brush masks (data URL) or rect
   *  coords; the new UI is brush-only. */
  maskDataUrl: string;
  prompt: string;
  negativePrompt?: string;
  /** When false, drops the Dark-Fantasy LoRA bias (raw FLUX-dev). */
  useLora?: boolean;
  /** Routes through the Fill workflow instead of dev. Set true for
   *  large or detailed inpaints. */
  useFillModel?: boolean;
  /** Sampler denoise — 0.85 default; lower preserves more of the
   *  source within the mask, higher gives the model more freedom. */
  denoise?: number;
  /** Optional reference + ControlNet bundle, same shape as generate. */
  refImageDataUrl?: string;
  refStrength?: number;
  useCannyLock?: boolean;
  cannyStrength?: number;
  controlnetModel?: string;
  seed?: number;
}

export interface ImageGenInpaintResult {
  galleryId: number | null;
  filename: string;
  workspaceRelativePath: string;
  elapsedMs: number;
}
export interface ImageGenGenerateResult {
  galleryId: number | null;
  galleryIds: number[];
}

// --- Admin ---
export interface AdminUser {
  id: string;
  createdAt: string;
  isAdmin: boolean;
  hasTotp: boolean;
  forceTotp: boolean;
  email: string | null;
}

/** Auth0 provisioning outcome returned by admin.createUser.
 *  - attempted: true only when a real-looking email was provided AND
 *    Auth0 mgmt is configured.
 *  - created: Auth0 user was minted (false for 409 — existing user,
 *    they still get a fresh reset email).
 *  - resetEmailSent: Auth0's "forgot your password?" email was fired
 *    for the address — the end user lands there and picks their own
 *    password. */
export interface AdminAuth0Status {
  configured: boolean;
  attempted: boolean;
  created: boolean;
  resetEmailSent: boolean;
  error: string | null;
  /** "email_not_deliverable_skipped_auth0" when the address didn't
   *  look real (test addresses, kid-device synthetic emails, etc). */
  note?: string;
}

export interface AdminCreateUserResult {
  user: AdminUser;
  email: string | null;
  auth0: AdminAuth0Status | null;
}
export interface AuditEntry {
  id: number;
  ts: string;
  actor: string;
  action: string;
  target: string | null;
  ip: string | null;
  details: Record<string, unknown> | null;
}
export interface AuditVerifyResult {
  ok: true;
  intact: boolean;
  brokenAt?: {
    id: number;
    ts: string;
    actor: string;
    action: string;
    expected: string;
    actual: string;
  };
}
export interface AdminChatSession {
  id: string;
  userId: string;
  name: string;
  kind: "chat" | "coding";
  messageCount: number;
  updatedAt: string;
  preview?: string;
}
export interface UsageStats {
  totals: {
    turns: number;
    prompt_tokens: number;
    completion_tokens: number;
    tool_calls: number;
  };
  perUser: Array<{ user_id: string; turns: number; prompt_tokens: number; completion_tokens: number }>;
  daily: Array<{ day: string; turns: number; tokens: number }>;
  topTools: Array<{ tool_name: string; n: number }>;
}
export interface LlmTarget {
  /** Internal target name, e.g. "default", "coder", "vision". */
  name: string;
  /** Human-readable label, e.g. "Default chat". */
  label?: string;
  /** Base URL the server forwards to. */
  baseUrl?: string;
  /** Model id the server sends. */
  model?: string;
  /** Currently-active target gets a true here. */
  active?: boolean;
}
export interface LlmStatusReport {
  /** Per-target reachability + latency. */
  targets?: Array<{ name: string; ok: boolean; latencyMs?: number; error?: string }>;
  /** Loosely-typed — server may grow this. */
  [k: string]: unknown;
}
export interface AdminBackup {
  /** Filename. */
  name: string;
  /** Full path on the server's disk. */
  path: string;
  /** Bytes. */
  size: number;
  /** ISO timestamp. */
  mtime: string;
}

// --- Alarms ---
// Mirrors the server's alarm row shape (see web_server.mjs#alarms.list).
// `kind: "once"` fires once at `fireAt` (unix ms); `kind: "daily"` fires
// at `atHHMM` (24-hour HH:MM, user-local) and reschedules itself.
export interface Alarm {
  id: string;
  name: string;
  body: string;
  kind: "once" | "daily";
  fireAt: number;
  atHHMM: string | null;
  toneUri: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// --- Device tokens + Secrets ---
export interface DeviceToken {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}
export interface SecretRef {
  name: string;
  updatedAt: string | null;
}

// A single row from gallery.list. settings is an opaque blob that
// downstream code may inspect (e.g. inpaint to extract source size).
export interface GalleryItem {
  id: number;
  workspaceRelativePath: string;
  prompt: string;
  positiveFull: string;
  negative: string;
  settings: Record<string, any> | null;
  createdAt: string;
}

// --- Meal plans ---
export type MealSlot = "breakfast" | "lunch" | "dinner";
export interface MealPlanEntry {
  id: string;
  date: string;            // YYYY-MM-DD
  slot: MealSlot;
  value: string;           // recipe name OR free-form note
  recipeId: string | null;
  /** Per-day overlay on the canonical recipe — swaps / removals /
   *  additions specific to this meal plan entry. NULL = no overlay. */
  substitutions?: MealPlanOverlay | null;
  createdAt?: string;
  updatedAt?: string;
  /** Client-only — see ShoppingItem._queued for details. */
  _queued?: boolean;
}

export interface MealPlanOverlay {
  version: 1;
  swaps?: Array<{ recipeItemId: number; originalText: string; substituteText: string; substituteQty?: string | null; note?: string | null; createdAt?: string }>;
  removals?: Array<{ recipeItemId: number; originalText: string; createdAt?: string }>;
  additions?: Array<{ id?: string; text: string; quantity?: string | null; createdAt?: string }>;
}

export interface EffectiveMealItem {
  id: number;
  text: string;
  quantity: string | null;
  position: number;
  category: string | null;
  emoji: string | null;
  isModified: boolean;
  modificationKind: null | "swap" | "addition";
  originalText: string | null;
}

export interface SubstitutionSuggestion {
  text: string;
  quantity: string | null;
  note: string | null;
}

/** One row from the AI plan-the-week proposal. day 0=Monday. */
export interface MealProposalRow {
  day: number;
  breakfast?: string;
  lunch?: string;
  dinner?: string;
}

// --- Recipes ---
export interface Recipe {
  id: string;
  name: string;
  imageUrl?: string | null;
  instructions?: string[] | null;
  notes?: string | null;
  /** How many people the recipe feeds (positive int) — pulled from
   *  the source recipeYield (JSON-LD), LLM extraction, or user entry.
   *  Drives the Recipes detail panel's auto-scale chip together with
   *  the user's household_size preference. Null = unset. */
  serves?: number | null;
  /** Auto-classified labels — fixed taxonomy (time/effort + cuisine +
   *  dish-type). See server src/recipes_classifier.RECIPE_TAGS for the
   *  full list. Null until the background classifier fires after the
   *  recipe is created/imported. */
  tags?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
  /** Owning household (v57). Null when viewing an anonymized catalogue
   *  recipe you don't own. */
  householdId?: number | null;
  /** Whether this recipe is published to the global catalogue. */
  inCatalogue?: boolean;
  /** If this is an imported copy, the catalogue master it came from. */
  sourceRecipeId?: number | null;
  /** Origin URL (recipe import) — only exposed to the owning household. */
  sourceUrl?: string | null;
}

/** A row in the global recipe catalogue — the anonymized read-only browse
 *  view. Never carries creator/household identifiers. */
export interface CatalogueRecipe {
  id: number;
  name: string;
  imageUrl?: string | null;
  instructions?: string[] | null;
  notes?: string | null;
  tags?: string[] | null;
  serves?: number | null;
  itemCount?: number | null;
  updatedAt?: string;
  /** Whether the requester's household already has this (master or copy). */
  owned: boolean;
}

/** One pantry-aware recipe suggestion returned by recipes.suggestions. */
export interface RecipeSuggestion {
  recipe: { id: number; name: string; imageUrl: string | null };
  coverage: number;
  matched: string[];
  missing: string[];
  topExpiringItems: Array<{ name: string; days: number | null }>;
  expiryUrgency: number;
  score: number;
}

// Tag taxonomy mirrored client-side so the Recipes page can group chips
// into "time/effort" and "cuisine"/"dish" rows. Kept in sync manually
// with src/recipes_classifier.RECIPE_TAGS. Plain string[] (not `as
// const`) so callers can pass `recipe.tags` (string[]) to `.includes()`
// without TS literal-type friction.
export const RECIPE_TAG_GROUPS: { time: string[]; cuisine: string[]; dish: string[] } = {
  time: ["weeknight", "quick", "veg", "weekend", "lazy"],
  cuisine: ["italian", "mexican", "asian", "american", "indian", "mediterranean", "french", "bbq", "middle-eastern"],
  dish: ["pizza", "pasta", "soup", "salad", "sandwich", "curry", "stir-fry", "casserole", "dessert", "breakfast"],
};

export interface RecipeItem {
  id: string;
  recipeId: string;
  text: string;
  quantity: string;
  position: number;
  category?: string | null;
  emoji?: string | null;
}

// --- Shopping ---
// Server uses sqlite INTEGER PKs for lists + items, so ids come back
// as numbers. Lock the types so we don't pass them as strings and
// trigger the wrong validator branches downstream.
export interface ShoppingList {
  id: number;
  name: string;
  archived?: boolean;
  /** Instance-wide visibility — every authenticated user sees this list.
   *  Rarely used; the per-list "shared_with_household" flag is the
   *  household-level default for collaboration. */
  isShared?: boolean;
  /** Household-level visibility (v50). When true, every member of the
   *  list's household sees it. When false, only the owner sees it
   *  (plus anyone in shopping_list_shares + instance-wide is_shared
   *  if those apply). */
  sharedWithHousehold?: boolean;
  /** The household this list lives in. Set at creation to the owner's
   *  active household. */
  householdId?: number | null;
  createdAt?: string;
}

export type ShoppingPlanMode = "week" | "recipes" | "tidy" | "free";

export interface ShoppingProposal {
  name: string;
  qty: string;
  reason: string;
}

export interface ShoppingItem {
  id: number;
  listId: number;
  text: string;
  quantity: string;
  purchased: boolean;
  position: number;
  category: string;
  emoji: string;
  addedBy?: string;  // userId of whoever added it (server-tagged)
  createdAt?: string;
  /** Server-stamped ISO timestamp of the most recent toggle to
   *  purchased=true. Cleared (null) when toggled back to false.
   *  Drives the "Got it" group's most-recently-checked sort. */
  purchasedAt?: string | null;
  /** Client-only flag, set when the row was created or edited while
   *  offline and is still waiting on the sync drainer to replay it.
   *  Cleared automatically once the server confirms the write and
   *  the cache mutator runs with the real response. */
  _queued?: boolean;
}

export interface TodoItem {
  id: string;
  listId: string;
  title: string;
  done: boolean;
  position: number;
  dueAt: string | null;
  priority: "low" | "med" | "high" | null;
  tags: string[];
  body: string;
  alarmEnabled: boolean;
  alarmId: string | null;
  /** RFC 5545 RRULE on an active recurring item (without the "RRULE:"
   *  prefix). Marking the item done auto-spawns a clone with `dueAt`
   *  advanced and the rrule preserved; the just-completed row's rrule
   *  goes null so it becomes pure history. v1 supports FREQ + BYDAY +
   *  BYMONTHDAY + UNTIL — see todos_store#_spawnNextRecurring. */
  rrule: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** Client-only — see ShoppingItem._queued for details. */
  _queued?: boolean;
}

export interface TodoTag {
  id: number;
  name: string;
  sortOrder: number;
  createdAt?: string;
}

export type RecipeTagCategory = "effort" | "cuisine" | "dish";

export interface RecipeTag {
  id: number;
  name: string;
  category: RecipeTagCategory;
  sortOrder: number;
  createdAt?: string;
}

// `admin` is the co-parent role: full day-to-day management (rename,
// add/remove members + kids, manage kid features, review kid chats).
// Destructive ops (delete household, transfer ownership, promote/demote
// admins) stay owner-only.
export type HouseholdRole = "owner" | "admin" | "member" | "kid";

export interface UserProfile {
  id: string;
  displayName: string;
  avatarColor: string | null;
  avatarEmoji: string | null;
  age: number | null;
}

export interface KidLoginEntry {
  id: string;
  displayName: string;
  avatarColor: string | null;
  avatarEmoji: string | null;
  // True when the kid has a PIN gate. False means tapping the avatar
  // signs straight in (only offered under an authenticated kid-device
  // session — the server enforces this).
  hasPin: boolean;
}

export interface Household {
  id: number;
  name: string;
  createdBy: string;
  createdAt: string;
  /** Caller's role in this household. */
  role: HouseholdRole;
  memberCount: number;
}

export interface HouseholdMember {
  userId: string;
  role: HouseholdRole;
  joinedAt: string;
}

/** One row per real household member returned by households.starsByMember.
 *  Excludes kid-device pseudo-users. `totalStars` is all-time across both
 *  kid chores (chore_completions, denied excluded) AND adult chores
 *  (adult_chore_completions). */
export interface FamilyStarsMember {
  userId: string;
  displayName: string;
  avatarEmoji: string | null;
  avatarColor: string | null;
  isKid: boolean;
  role: HouseholdRole;
  /** Reward balance — earned + grants − reward redemptions. The chip's
   *  "⭐ N". Lowered only by reward claims, NOT by cash-outs. */
  totalStars: number;
  /** Cashable balance — earned + grants − cash-outs (pending+approved).
   *  Independent of totalStars: cashing out lowers this, not the reward
   *  balance. Drives the cash-out flow's capacity. */
  cashableStars: number;
  /** Non-decreasing achievement count. Lifetime stars ever earned;
   *  cash-outs and other deductions don't reduce it. */
  lifetimeStars: number;
  /** Bank balance in cents. */
  dollarsCents: number;
}

export interface BankPendingRequest {
  requestId: number;
  requestedByUserId: string;
  requestedByName: string;
  starsAmount: number;
  rateAtRequest: number;
  dollarsCents: number;
  requestedAt: string;
}

export interface BankLedgerEntry {
  id: number;
  deltaCents: number;
  reason: string | null;
  sourceRequestId: number | null;
  createdByUserId: string;
  createdAt: string;
}

export interface BankMyPending {
  requestId: number;
  starsAmount: number;
  rateAtRequest: number;
  dollarsCents: number;
  requestedAt: string;
}

export interface AdminHousehold {
  id: number;
  name: string;
  createdBy: string;
  createdAt: string;
  memberCount: number;
  mealPlanCount: number;
  pantryCount: number;
  shoppingCount: number;
}

// v51 — chores (household-scoped catalog + per-day completion log).
export type ChoreFrequency = "daily" | "weekly" | "biweekly" | "rrule";

export interface Chore {
  id: number;
  householdId: number;
  name: string;
  icon: string | null;
  assignedToUserId: string | null;
  frequency: ChoreFrequency;
  rrule: string | null;
  stars: number;
  requiresApproval: boolean;
  createdByUserId: string | null;
  createdAt: string;
  archivedAt: string | null;
  /** Only populated by listForHousehold (admin chart). null elsewhere. */
  pendingApprovals?: number | null;
}

export interface KidChore extends Chore {
  /** True if this kid has a UN-denied completion row for the queried
   *  date. Denied completions don't count — they keep `completionId`
   *  set (audit trail) but `doneToday` flips back to false so the
   *  kid's tile shows un-done with the denial banner. */
  doneToday: boolean;
  completionId: number | null;
  completedAt: string | null;
  approvedAt: string | null;
  /** ISO time of denial, null if not denied. Indicates the parent
   *  rejected this completion attempt — kid should redo and re-tap. */
  deniedAt: string | null;
  /** Optional 1-line reason the parent left on the denial. */
  denialReason: string | null;
  starsGranted: number | null;
}

export interface PendingApproval {
  completionId: number;
  choreId: number;
  choreName: string;
  choreIcon: string | null;
  choreStars: number;
  assignedTo: string | null;
  completedBy: string;
  completedAt: string;
  dueOn: string;
}

export interface ToggleDoneResult {
  ok: true;
  action: "done" | "undone";
  choreId: number;
  dueOn: string;
  completionId?: number;
  starsAwarded?: number;
  pendingApproval?: boolean;
}

// v54 — adult chores (partner-share chart, separate from kids' chart).
export type AdultChoreFrequency = "daily" | "weekly" | "monthly" | "quarterly" | "once";

// v55 — rewards. Catalog rows + redemption shapes.
export interface Reward {
  id: number;
  householdId: number;
  name: string;
  description: string | null;
  icon: string | null;
  costStars: number;
  requiresApproval: boolean;
  maxPerWeek: number | null;
  createdByUserId: string | null;
  createdAt: string;
  archivedAt: string | null;
}

export type RedemptionStatus = "pending" | "approved" | "denied" | "fulfilled";

export interface PendingRedemption {
  id: number;
  rewardId: number;
  rewardName: string;
  rewardIcon: string | null;
  redeemedBy: string;
  costStars: number;
  note: string | null;
  requestedAt: string;
}

export interface MyRedemption {
  id: number;
  rewardId: number;
  rewardName: string;
  rewardIcon: string | null;
  costStars: number;
  status: RedemptionStatus;
  requestedAt: string;
  decidedAt: string | null;
}

// Unified row used by the Chores tab's All view — combines kid +
// adult chores into one display-oriented shape. The server's
// chores.allForHousehold RPC normalizes both tables into this.
//
// `id` is namespaced ("kid:N" / "adult:N") so the client can
// dispatch the right mutator without inspecting `kind` separately.
// `rawId` is the un-prefixed numeric id for that table.
export type UnifiedChoreFrequency = "daily" | "weekly" | "biweekly" | "monthly" | "quarterly" | "once" | "rrule";

export interface UnifiedChore {
  id: string;
  rawId: number;
  kind: "kid" | "adult";
  name: string;
  icon: string | null;
  /** For single mode this is the user id. For rotating it's the
   *  resolved this-week owner. For parallel it's null — consult
   *  `assignees` instead. */
  assignee: string | null;
  /** "single" | "rotating" (adult only) | "parallel". */
  assignmentMode: "single" | "rotating" | "parallel";
  /** User-id list for parallel chores. Empty for single + rotating. */
  assignees: string[];
  /** User ids who've completed THIS chore today (kid + adult). Lets
   *  parallel-mode UI hide rows the user has already done while the
   *  partner still sees them. */
  completedTodayBy: string[];
  frequency: UnifiedChoreFrequency;
  stars: number;
  requiresApproval: boolean;
  hue: number | null;
  category: string | null;
  lastDone: string | null;
  nextDue: string | null;
  doneToday: boolean;
  pendingApproval: boolean;
}

export interface AdultChore {
  id: number;
  householdId: number;
  name: string;
  /** Optional 1-2 char emoji icon shown on the card. */
  icon: string | null;
  category: string | null;
  frequency: AdultChoreFrequency;
  /** User id, or the literal sentinel "rotates" / "parallel". For
   *  single-mode chores this is the user id. For rotating it's
   *  "rotates" and `thisWeek` resolves the owner. For parallel it's
   *  "parallel" and `assignees` carries the user-id list. */
  assignee: string;
  /** "single" | "rotating" | "parallel". */
  assignmentMode: "single" | "rotating" | "parallel";
  /** User-id list for parallel chores. Empty for single + rotating. */
  assignees: string[];
  /** User ids who have completed THIS chore today. Used by parallel-
   *  mode rendering to hide rows the caller has already done — the
   *  partner still sees the chore until they personally complete it. */
  completedTodayBy: string[];
  /** Server-resolved owner for the current week. For fixed assignees
   *  this equals `assignee`; for `"rotates"` it flips by ISO week. */
  thisWeek: string | null;
  /** OKLCH hue 0-360 for the per-chore color stripe. */
  hue: number;
  /** Stars granted to whoever marks it done. 0 = no gamification. */
  stars: number;
  /** ISO date of last completion, or null if never. */
  lastDone: string | null;
  /** ISO date when the chore comes due next, derived from
   *  last_done + frequency interval. */
  nextDue: string;
  createdByUserId: string | null;
  createdAt: string;
}

export interface Skill {
  name: string;
  description: string;
  body: string;
  scope: "user" | "global" | "builtin";
  argumentHint?: string;
  mtime?: number;
}

export type MemoryType = "user" | "feedback" | "project" | "reference";

export interface MemoryMeta {
  name: string;
  description: string;
  type: MemoryType;
  updatedAt: string;
}

export interface Memory extends MemoryMeta {
  body: string;
}

export interface PresetMeta {
  name: string;
  description: string;
  updatedAt?: string;
}

export interface Preset extends PresetMeta {
  body: string;
}
